<div class="page-head">
  <div>
    <p class="eyebrow">{{ booking() ? bookingTypeLabel() : 'รายละเอียด Booking' }}</p>
    <h1>{{ booking()?.bookingNo || 'รายละเอียดการจอง' }}</h1>
    <p>ข้อมูลการจอง ลูกค้า การเดินทาง และภาพรวมทางการเงิน</p>
  </div>
  <div class="flex items-center gap-2">
    <app-refresh-button
      class="detail-floating-refresh fixed top-24 z-20 -translate-x-1/2 transition-opacity duration-200 sm:top-28"
      [class.pointer-events-none]="isRefreshButtonHidden()"
      [class.opacity-0]="isRefreshButtonHidden()"
      label="รีเฟรช"
      [loading]="isRefreshing()"
      [prominent]="true"
      (refresh)="refreshBookingDetails()"
    />
    <a
      [routerLink]="bookingListUrl()"
      class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-slate-700 no-underline transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
    >
      <lucide-icon name="ArrowLeft" [size]="17" aria-hidden="true" /> กลับหน้ารายการ
    </a>
  </div>
</div>

@if (booking(); as booking) {
  @if (canViewFullFinancialSummary()) {
    <section
      class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:[grid-template-columns:repeat(auto-fit,minmax(0,1fr))]"
    >
      <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3">
          <p class="m-0 text-sm font-semibold text-slate-600">ยอดขายสุทธิ</p>
          <span
            class="grid size-9 place-items-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-200"
          >
            <lucide-icon name="CircleDollarSign" [size]="18" aria-hidden="true" />
          </span>
        </div>
        <strong class="mt-3 block text-xl text-slate-800"
          >{{ financialSummary().netSales | number: '1.2-2' }} บาท</strong
        >
        <small class="mt-1 block text-slate-500">รวมยอดเรียกเก็บเพิ่มที่อนุมัติแล้ว</small>
      </article>

      <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3">
          <p class="m-0 text-sm font-semibold text-slate-600">รับชำระที่ตรวจสอบแล้ว</p>
          <span
            class="grid size-9 place-items-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          >
            <lucide-icon name="CircleCheck" [size]="18" aria-hidden="true" />
          </span>
        </div>
        <strong class="mt-3 block text-xl text-slate-800"
          >{{ financialSummary().verifiedPayment | number: '1.2-2' }} บาท</strong
        >
        <small class="mt-1 block text-slate-500">หลังหักคืนเงินที่จ่ายสำเร็จ</small>
      </article>

      @if (!isInHouseTour()) {
        <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div class="flex items-center justify-between gap-3">
            <p class="m-0 text-sm font-semibold text-slate-600">ค่าใช้จ่าย</p>
            <span
              class="grid size-9 place-items-center rounded-xl bg-red-50 text-red-700 ring-1 ring-red-200"
            >
              <lucide-icon name="FileText" [size]="18" aria-hidden="true" />
            </span>
          </div>
          <strong class="mt-3 block text-xl text-slate-800"
            >{{ paidExpenseTotal() | number: '1.2-2' }} บาท</strong
          >
          <small class="mt-1 block text-slate-500">ยอดโอนจ่ายแล้ว</small>
        </article>

        <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div class="flex items-center justify-between gap-3">
            <p class="m-0 text-sm font-semibold text-slate-600">กำไร</p>
            <span
              class="grid size-9 place-items-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-violet-200"
            >
              <lucide-icon name="BadgePercent" [size]="18" aria-hidden="true" />
            </span>
          </div>
          <strong
            class="mt-3 block text-xl"
            [class.text-emerald-700]="financialSummary().actualProfit >= 0"
            [class.text-red-600]="financialSummary().actualProfit < 0"
          >
            {{ financialSummary().actualProfit | number: '1.2-2' }} บาท
          </strong>
          <small class="mt-1 block text-slate-500">ยอดรับจริงหักยอดโอนจ่ายและค่าธรรมเนียม</small>
        </article>
      }
    </section>
  }

  <div class="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
    <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div
        class="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4"
      >
        <div>
          <h2 class="m-0 text-lg font-bold text-slate-800">ข้อมูล Booking</h2>
          <p class="mt-1 mb-0 text-xs leading-5 text-slate-500">
            รายละเอียดการจอง ลูกค้า และการเดินทาง
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <app-status-badge [status]="booking.paymentStatus" />
          <app-status-badge [status]="booking.status" />
        </div>
      </div>

      <div class="grid gap-6">
        <section>
          <dl
            class="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3 [&>div]:py-3 [&_dt]:text-xs [&_dt]:leading-5 [&_dt]:font-medium [&_dt]:text-slate-500 [&_dd]:text-sm [&_dd]:leading-5"
          >
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">เลขที่ Booking</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ booking.bookingNo }}</dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ประเภท Booking</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ bookingTypeLabel() }}</dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">วันที่จอง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.bookingDate | date: 'dd MMM yyyy' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">วันที่สร้างข้อมูล</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.createdAt | date: 'dd MMM yyyy, HH:mm' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">พนักงานขาย</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ booking.saleName || '-' }}</dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ผู้บันทึก</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.createdByName || '-' }}
              </dd>
            </div>
          </dl>
        </section>

        <section>
          <h3 class="m-0 border-b border-slate-200 pb-3 text-[15px] font-bold text-slate-800">
            ข้อมูลลูกค้า
          </h3>
          <dl
            class="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3 [&>div]:py-3 [&_dt]:text-xs [&_dt]:leading-5 [&_dt]:font-medium [&_dt]:text-slate-500 [&_dd]:text-sm [&_dd]:leading-5"
          >
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">รหัสลูกค้า</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.customerCode || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ประเภทลูกค้า</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.customerType === 'CORPORATE' ? 'นิติบุคคล' : 'บุคคลธรรมดา' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ชื่อผู้จอง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.customerName || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">เลขประจำตัวผู้เสียภาษี</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.customerTaxId || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">เบอร์โทร</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.customerPhone || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">อีเมล</dt>
              <dd class="mt-1 mb-0 break-all font-semibold text-slate-800">
                {{ booking.customerEmail || '-' }}
              </dd>
            </div>
          </dl>
        </section>

        <section>
          <h3 class="m-0 border-b border-slate-200 pb-3 text-[15px] font-bold text-slate-800">
            ข้อมูลทัวร์และการเดินทาง
          </h3>
          <dl
            class="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3 [&>div]:py-3 [&_dt]:text-xs [&_dt]:leading-5 [&_dt]:font-medium [&_dt]:text-slate-500 [&_dd]:text-sm [&_dd]:leading-5"
          >
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">รหัสทัวร์</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ booking.tourCode || '-' }}</dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ประเทศ</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.countryName || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">ชื่อโปรแกรม</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.programName || '-' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">จำนวนผู้เดินทาง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.travelerCount | number }} คน
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">โฮลเซล</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{
                  booking.wholesaleName ||
                    (booking.bookingType === 'GROUP_TOUR' ? 'ไม่ระบุสำหรับเหมากรุ๊ป' : '-')
                }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">วันเดินทางไป</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.departureDate | date: 'dd MMM yyyy' }}
              </dd>
            </div>
            <div class="border-b border-slate-100 py-3 sm:pr-6">
              <dt class="text-xs text-slate-500">วันเดินทางกลับ</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ booking.returnDate ? (booking.returnDate | date: 'dd MMM yyyy') : '-' }}
              </dd>
            </div>
            @if (!isInHouseTour()) {
              <div class="border-b border-slate-100 py-3 sm:pr-6">
                <dt class="text-xs text-slate-500">ผู้ดูแลรายการนี้</dt>
                <dd
                  class="mt-1 mb-0 flex flex-wrap items-center justify-between gap-2 font-semibold text-slate-800"
                >
                  <span>{{ booking.collaboratorName || '-' }}</span>
                  @if (canManageBooking()) {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      [attr.aria-label]="
                        booking.collaboratorName ? 'เปลี่ยนผู้ดูแล' : 'เพิ่มผู้ดูแล'
                      "
                      [title]="booking.collaboratorName ? 'เปลี่ยนผู้ดูแล' : 'เพิ่มผู้ดูแล'"
                      (click)="openCollaboratorModal()"
                    >
                      <lucide-icon
                        [name]="booking.collaboratorName ? 'Pencil' : 'UserPlus'"
                        [size]="15"
                        aria-hidden="true"
                      />
                    </button>
                  }
                </dd>
              </div>
            }
            @if (!isInHouseTour()) {
              <div class="border-b border-slate-100 py-3 sm:pr-6">
                <dt class="text-xs text-slate-500">เอกสารรับค่าคอม</dt>
                <dd
                  class="mt-1 mb-0 flex flex-wrap items-center justify-between gap-2 font-semibold text-slate-800"
                >
                  @if (booking.commissionAttachmentPath; as attachmentPath) {
                    <a
                      [href]="commissionAttachmentUrl(attachmentPath)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 text-sky-700 no-underline hover:underline"
                    >
                      มีเอกสารแล้ว <lucide-icon name="ExternalLink" [size]="14" />
                    </a>
                  } @else {
                    <span>{{ bookingCommissionDocumentFile() ? 'เลือกเอกสารแล้ว' : '-' }}</span>
                  }
                  @if (canManageBooking()) {
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      [attr.aria-label]="
                        booking.commissionAttachmentPath || bookingCommissionDocumentFile()
                          ? 'เปลี่ยนเอกสารรับค่าคอม'
                          : 'เพิ่มเอกสารรับค่าคอม'
                      "
                      [title]="
                        booking.commissionAttachmentPath || bookingCommissionDocumentFile()
                          ? 'เปลี่ยนเอกสารรับค่าคอม'
                          : 'เพิ่มเอกสารรับค่าคอม'
                      "
                      (click)="isBookingCommissionDocumentModalOpen.set(true)"
                    >
                      <lucide-icon
                        [name]="
                          booking.commissionAttachmentPath || bookingCommissionDocumentFile()
                            ? 'Pencil'
                            : 'Plus'
                        "
                        [size]="15"
                        aria-hidden="true"
                      />
                    </button>
                  }
                </dd>
              </div>
            }
            <div class="grid gap-x-6 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
              @if (!booking.tourId) {
                <div class="border-b border-slate-100 py-3 sm:pr-6">
                  <dt class="text-xs text-slate-500">คอมมิชชั่นบริษัท</dt>
                  <dd class="mt-1 mb-0 font-semibold text-slate-800">
                    {{
                      booking.companyCommission === null
                        ? '-'
                        : (booking.companyCommission | number: '1.2-2') + ' บาท'
                    }}
                  </dd>
                </div>
              }
              @if (booking.bookingType !== 'GROUP_TOUR') {
                <div class="border-b border-slate-100 py-3 sm:pr-6">
                  <dt class="text-xs text-slate-500">
                    {{ booking.tourId ? 'คอมมิชชั่น Sale จาก Tour' : 'คอมมิชชั่นพนักงาน' }}
                  </dt>
                  <dd class="mt-1 mb-0 font-semibold text-slate-800">
                    {{
                      booking.saleCommission === null
                        ? '-'
                        : (booking.saleCommission | number: '1.2-2') + ' บาท'
                    }}
                  </dd>
                </div>
              }
              @if (!booking.tourId) {
                <div class="border-b border-slate-100 py-3 sm:pr-6">
                  <dt class="text-xs text-slate-500">คอมมิชชั่นพิเศษ</dt>
                  <dd class="mt-1 mb-0 font-semibold text-slate-800">
                    {{
                      booking.specialCommission === null
                        ? '-'
                        : (booking.specialCommission | number: '1.2-2') + ' บาท'
                    }}
                  </dd>
                </div>
              }
            </div>
          </dl>
        </section>
      </div>
    </section>

    @if (canViewFullFinancialSummary()) {
      <aside
        class="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:top-5 lg:justify-self-end"
      >
        <h2 class="m-0 text-lg font-bold text-slate-800">สรุปราคาและงบประมาณ</h2>
        <div class="mt-5 grid gap-4 text-sm">
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">จำนวนคน</span
            ><strong class="text-slate-800">{{ booking.travelerCount | number }} คน</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาทัวร์ต่อคน</span
            ><strong class="text-slate-800"
              >{{ booking.pricePerPerson | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาทัวร์ทั้งหมด</span
            ><strong class="text-slate-800">{{ grossTourAmount() | number: '1.2-2' }} บาท</strong>
          </div>
          @if (isInHouseTour() && approvedTourPriceAmount() > 0) {
            <div class="flex items-center justify-between gap-4">
              <span class="text-slate-500">ราคาเพิ่มทั้งหมดของทัวร์</span>
              <strong class="text-violet-700"
                >+{{ approvedTourPriceAmount() | number: '1.2-2' }} บาท</strong
              >
            </div>
          }
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ส่วนลดทั้งหมด</span
            ><strong class="text-red-600">-{{ totalDiscount() | number: '1.2-2' }} บาท</strong>
          </div>

          <div class="border-t border-slate-200 pt-4">
            @if (approvedCustomerChargeDelta() > 0) {
              <div class="mb-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">ยอดเรียกเก็บเพิ่มที่อนุมัติ</span>
                <strong class="text-slate-800">
                  +{{ approvedCustomerChargeDelta() | number: '1.2-2' }} บาท
                </strong>
              </div>
            }
            <div class="flex items-end justify-between gap-4">
              <span class="font-semibold text-slate-700">ยอดรวมสุทธิ</span>

              <strong class="text-xl text-sky-700"
                >{{ financialSummary().netSales | number: '1.2-2' }} บาท</strong
              >
            </div>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">รับชำระแล้ว</span>
            <strong class="text-emerald-700"
              >{{ financialSummary().verifiedPayment | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">หัก ณ ที่จ่าย</span>
            <strong class="text-red-600"
              >-{{ verifiedWithholdingTaxTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          @if (!isInHouseTour()) {
            <div class="flex items-center justify-between gap-4">
              <span class="text-slate-500">ใช้จ่ายแล้ว</span>
              <strong class="text-red-600">{{ spentTotal() | number: '1.2-2' }} บาท</strong>
            </div>
          }
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">คืนเงิน</span>
            <strong class="text-red-600">{{ activeRefundTotal() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ค่าธรรมเนียม (ลูกค้า)</span>
            <strong class="text-slate-800"
              >{{ verifiedCustomerCreditCardFeeTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ค่าธรรมเนียม (บริษัท)</span>
            <strong class="text-red-600"
              >{{ verifiedCompanyCreditCardFeeTotal() | number: '1.2-2' }} บาท</strong
            >
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ยอดคงเหลือ</span>
            <strong class="text-amber-700"
              >{{ adjustedRemainingBalance() | number: '1.2-2' }} บาท</strong
            >
          </div>
          @if (isInHouseTour() && booking.bookingType !== 'GROUP_TOUR') {
            <div class="border-t border-slate-200 pt-4">
              <div class="flex items-center justify-between gap-4">
                <span class="font-semibold text-slate-700">คอมมิชชั่นที่ได้รับทั้งหมด</span>
                <strong class="text-emerald-700"
                  >+{{
                    (booking.saleCommission ?? 0) | number: '1.2-2'
                  }}
                  บาท</strong
                >
              </div>
            </div>
          }
          <div
            [class.border-t]="booking.bookingType === 'JOIN_TOUR' && !booking.tourId"
            [class.border-slate-200]="booking.bookingType === 'JOIN_TOUR' && !booking.tourId"
            [class.pt-4]="booking.bookingType === 'JOIN_TOUR' && !booking.tourId"
          >
            @if (!isInHouseTour() && approvedExpenseBudgetDelta() > 0) {
              <div class="mb-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">วงเงินค่าใช้จ่ายเพิ่มที่อนุมัติ</span>
                <strong class="text-slate-800">
                  +{{ approvedExpenseBudgetDelta() | number: '1.2-2' }} บาท
                </strong>
              </div>
            }
            @if (!isInHouseTour()) {
              <div class="flex items-center justify-between gap-4">
                <span class="text-slate-500">วงเงินค่าใช้จ่ายปัจจุบัน</span>
                <strong class="text-slate-800"
                  >{{ financialSummary().estimatedExpense | number: '1.2-2' }} บาท</strong
                >
              </div>
            }
            @if (booking.bookingType === 'JOIN_TOUR' && !booking.tourId) {
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">ค่าคอมพนักงาน</span>
                <strong class="text-red-600">
                  -{{ employeeCommissionTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
            }
            @if (!isInHouseTour() && approvedCompanyAdvanceAmount() > 0) {
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="text-slate-500">เงินสำรองที่อนุมัติ</span>
                <strong class="text-slate-800">
                  {{ approvedCompanyAdvanceAmount() | number: '1.2-2' }} บาท
                </strong>
              </div>
            }
            @if (!isInHouseTour()) {
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="font-semibold text-slate-700">กำไร</span>
                <strong
                  [class.text-emerald-700]="financialSummary().actualProfit >= 0"
                  [class.text-red-600]="financialSummary().actualProfit < 0"
                  >{{ financialSummary().actualProfit | number: '1.2-2' }} บาท</strong
                >
              </div>
            }
          </div>
          <div class="border-t border-slate-200 pt-4">
            @if (booking.bookingType === 'GROUP_TOUR' && canViewCloseBookingCase()) {
              <button
                type="button"
                class="tw-button inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="!canCloseGroupBookingCase() || isClosingGroupBookingCase()"
                [title]="closeBookingCaseButtonTitle()"
                (click)="closeGroupBookingCase()"
              >
                <lucide-icon name="CircleCheck" [size]="17" aria-hidden="true" />
                ปิดเคส
              </button>
            } @else if (booking.bookingType === 'JOIN_TOUR' && canViewCloseBookingCase()) {
              <button
                type="button"
                class="tw-button inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="!canCloseJoinBookingCase() || isClosingJoinBookingCase()"
                [title]="closeBookingCaseButtonTitle()"
                (click)="closeJoinBookingCase()"
              >
                <lucide-icon name="CircleCheck" [size]="17" aria-hidden="true" />
                ปิดเคส
              </button>
            }
            @if (booking.status === 'COMPLETED') {
              <button
                type="button"
                class="tw-button mt-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 text-sm font-semibold text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled
                title="ยังไม่เปิดใช้งาน"
              >
                <lucide-icon name="Undo2" [size]="17" aria-hidden="true" />
                เปิดเคส
              </button>
            }
          </div>
        </div>
      </aside>
    } @else {
      <aside
        class="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:top-5 lg:justify-self-end"
      >
        <h2 class="m-0 text-lg font-bold text-slate-800">สรุปราคา</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">ยอดที่พนักงานขายต้องเรียกเก็บจากลูกค้า</p>

        <div class="mt-5 grid gap-4 text-sm">
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ราคาทัวร์ก่อนหักส่วนลด</span>
            <strong class="text-slate-800">{{ grossTourAmount() | number: '1.2-2' }} บาท</strong>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-slate-500">ส่วนลดทั้งหมด</span>
            <strong class="text-red-600">-{{ totalDiscount() | number: '1.2-2' }} บาท</strong>
          </div>
          @if (canViewExpenseSection()) {
            <div class="border-t border-slate-200 pt-4">
              <h3 class="m-0 text-sm font-bold text-slate-800">สรุปค่าใช้จ่าย</h3>
              <div class="mt-3 grid gap-3">
                <div class="flex items-center justify-between gap-4">
                  <span class="text-slate-500">วงเงินค่าใช้จ่ายทั้งหมด</span>
                  <strong class="text-slate-800">
                    {{ adjustedExpenseBudget() | number: '1.2-2' }} บาท
                  </strong>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-slate-500">ยอดที่ขอเบิกแล้ว</span>
                  <strong class="text-red-600">
                    {{ activeExpenseTotal() | number: '1.2-2' }} บาท
                  </strong>
                </div>
                <div class="flex items-end justify-between gap-4 border-t border-slate-100 pt-3">
                  <span class="font-semibold text-slate-700">วงเงินคงเหลือ</span>
                  <strong class="text-xl text-emerald-700">
                    {{ expenseBudgetRemaining() | number: '1.2-2' }} บาท
                  </strong>
                </div>
                @if (expenseBudgetShortfall() > 0) {
                  <div class="flex items-center justify-between gap-4">
                    <span class="font-semibold text-red-600">ยอดเกินวงเงิน</span>
                    <strong class="text-red-600">
                      {{ expenseBudgetShortfall() | number: '1.2-2' }} บาท
                    </strong>
                  </div>
                }
              </div>
            </div>
          }
          <div class="border-t border-slate-200 pt-4">
            <h3 class="m-0 text-sm font-bold text-slate-800">สรุปยอดชำระ</h3>
            <div class="mt-3 grid gap-3">
              <div class="flex items-center justify-between gap-4">
                <span class="text-slate-500">ยอดที่ต้องชำระทั้งหมด</span>
                <strong class="text-slate-800">
                  {{ adjustedBookingTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-slate-500">ยอดที่ชำระเข้ามาแล้ว</span>
                <strong class="text-emerald-700">
                  {{ verifiedPaymentTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-slate-500">ค่าธรรมเนียมที่รับจากลูกค้า</span>
                <strong class="text-slate-800">
                  {{ verifiedCustomerCreditCardFeeTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="flex items-center justify-between gap-4">
                <span class="text-slate-500">ค่าธรรมเนียมที่บริษัทรับภาระ</span>
                <strong class="text-red-600">
                  {{ verifiedCompanyCreditCardFeeTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div class="flex items-end justify-between gap-4 border-t border-slate-100 pt-3">
                <span class="font-semibold text-slate-700">ยอดที่ยังขาด</span>
                <strong class="text-xl text-red-600">
                  {{ verifiedPaymentRemaining() | number: '1.2-2' }} บาท
                </strong>
              </div>
            </div>
          </div>
          <div class="border-t border-slate-200 pt-4">
            @if (booking.bookingType === 'GROUP_TOUR' && canViewCloseBookingCase()) {
              <button
                type="button"
                class="tw-button inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="!canCloseGroupBookingCase() || isClosingGroupBookingCase()"
                [title]="closeBookingCaseButtonTitle()"
                (click)="closeGroupBookingCase()"
              >
                <lucide-icon name="CircleCheck" [size]="17" aria-hidden="true" />
                ปิดเคส
              </button>
            } @else if (booking.bookingType === 'JOIN_TOUR' && canViewCloseBookingCase()) {
              <button
                type="button"
                class="tw-button inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="!canCloseJoinBookingCase() || isClosingJoinBookingCase()"
                [title]="closeBookingCaseButtonTitle()"
                (click)="closeJoinBookingCase()"
              >
                <lucide-icon name="CircleCheck" [size]="17" aria-hidden="true" />
                ปิดเคส
              </button>
            }
            @if (booking.status === 'COMPLETED') {
              <button
                type="button"
                class="tw-button mt-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 text-sm font-semibold text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled
                title="ยังไม่เปิดใช้งาน"
              >
                <lucide-icon name="Undo2" [size]="17" aria-hidden="true" />
                เปิดเคส
              </button>
            }
          </div>
        </div>
      </aside>
    }
  </div>

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">สมาชิกทัวร์</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">
          @if (booking.bookingType === 'GROUP_TOUR') {
            จำนวนผู้เดินทาง {{ booking.travelerCount }} คน
          } @else {
            รายชื่อผู้เดินทาง {{ tourMembers().length }} / {{ booking.travelerCount }} คน
          }
        </p>
      </div>
      @if (canManageBooking()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          style="font-size: 15px"
          [disabled]="isTourMemberLimitReached()"
          [title]="
            isTourMemberLimitReached() ? 'จำนวนสมาชิกครบตาม Booking แล้ว' : 'เพิ่มสมาชิกทัวร์'
          "
          (click)="addTourMember()"
        >
          <lucide-icon name="UserPlus" [size]="17" aria-hidden="true" />
          เพิ่มสมาชิกทัวร์
        </button>
      }
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[1180px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="min-w-56 px-4 py-3">ผู้เดินทาง</th>
            <th class="w-44 px-4 py-3">เลขบัตรประชาชน / เลขประจำตัวผู้เสียภาษี</th>
            <th class="w-36 px-4 py-3">เบอร์โทร</th>
            <th class="w-36 px-4 py-3">พาสปอร์ต</th>
            <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            <th class="w-24 px-4 py-3 text-right">ดำเนินการ</th>
          </tr>
        </thead>
        <tbody>
          @for (member of tourMembers(); track member.id; let index = $index) {
            <tr class="border-t border-slate-100 transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-3 text-center tabular-nums text-slate-400">{{ index + 1 }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="font-semibold text-slate-800">{{ member.fullName }}</span>
                  @if (member.isLeadTraveler) {
                    <span
                      class="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-200"
                      >ผู้ติดต่อหลัก</span
                    >
                  }
                </div>
              </td>
              <td class="px-4 py-3 font-medium tracking-wide text-slate-600 tabular-nums">
                {{ member.citizenId || '—' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-slate-600 tabular-nums">
                {{ member.phoneNumber || '—' }}
              </td>
              <td class="px-4 py-3">
                @if (member.passportFileName) {
                  <a
                    [href]="passportUrl(member.passportFileName)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:text-sky-800 hover:underline"
                    >เปิดไฟล์
                    <lucide-icon name="ExternalLink" [size]="13" />
                  </a>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-4 py-3 text-center">
                <app-status-badge
                  [status]="member.status"
                  [displayLabel]="member.status === 'COMPLETED' ? 'บันทึกข้อมูล' : undefined"
                  [tone]="member.status === 'COMPLETED' ? 'success' : undefined"
                />
              </td>

              <td class="px-4 py-3">
                @if (canManageBooking()) {
                  <div class="flex justify-end gap-1.5">
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                      title="แก้ไขสมาชิก"
                      (click)="editTourMember(member)"
                    >
                      <lucide-icon name="Pencil" [size]="15" /></button
                    ><button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white p-0 text-red-600 hover:bg-red-50"
                      title="ลบสมาชิก"
                      (click)="deleteTourMember(member)"
                    >
                      <lucide-icon name="Trash2" [size]="15" />
                    </button>
                  </div>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="7" class="px-4 py-10 text-center text-[13px] text-slate-500">
                ยังไม่มีสมาชิกทัวร์ใน Booking นี้
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (canViewFinancialAdjustments() && !isInHouseTour()) {
    <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
      >
        <div>
          <h2 class="m-0 text-lg font-bold text-slate-800">ปรับวงเงินและเงินสำรอง</h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            คำขอที่เกี่ยวข้องกับ Booking นี้ {{ financialAdjustments().length }} รายการ
          </p>
        </div>
        @if (canCreateFinancialAdjustment()) {
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] sm:self-auto"
            (click)="openFinancialAdjustmentModal()"
          >
            <lucide-icon name="Plus" [size]="17" aria-hidden="true" />
            สร้างคำขอ
          </button>
        }
      </div>

      <div class="overflow-x-auto">
        <table class="w-full min-w-[920px] border-collapse text-left text-[13px]">
          <thead
            class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
          >
            <tr>
              <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
              <th class="w-40 px-4 py-3">คำขอ</th>
              <th class="min-w-72 px-4 py-3">รายละเอียด</th>
              <th class="w-64 px-4 py-3">รายการปรับ</th>
              <th class="w-28 px-4 py-3 text-center">เอกสาร</th>
              <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            @for (adjustment of financialAdjustments(); track adjustment.id; let index = $index) {
              <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
                <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                  {{ index + 1 }}
                </td>
                <td class="px-4 py-4 align-top">
                  <span class="font-semibold whitespace-nowrap text-sky-700">
                    {{ adjustment.adjustmentNo }}
                  </span>
                </td>
                <td class="px-4 py-4 align-top">
                  <p class="m-0 leading-5 font-medium text-slate-700">{{ adjustment.reason }}</p>
                  <div
                    class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500"
                  >
                    <span>
                      ผู้ขอ: {{ adjustment.requestedBy.firstName }}
                      {{ adjustment.requestedBy.lastName }}
                    </span>
                    <span class="whitespace-nowrap">
                      {{ adjustment.createdAt | date: 'dd MMM yyyy, HH:mm' }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-4 align-top">
                  <div class="grid gap-1.5">
                    @if (adjustmentAmount(adjustment.expenseBudgetDelta) > 0) {
                      <div
                        class="flex items-center justify-between gap-4 rounded-lg bg-red-50 px-2.5 py-1.5"
                      >
                        <span class="text-xs text-red-700">วงเงินค่าใช้จ่าย</span>
                        <span class="font-semibold whitespace-nowrap text-red-700 tabular-nums">
                          +{{ adjustment.expenseBudgetDelta | number: '1.2-2' }} บาท
                        </span>
                      </div>
                    }
                    @if (adjustmentAmount(adjustment.customerChargeDelta) > 0) {
                      <div
                        class="flex items-center justify-between gap-4 rounded-lg bg-emerald-50 px-2.5 py-1.5"
                      >
                        <span class="text-xs text-emerald-700">ยอดเรียกเก็บ</span>
                        <span class="font-semibold whitespace-nowrap text-emerald-700 tabular-nums">
                          +{{ adjustment.customerChargeDelta | number: '1.2-2' }} บาท
                        </span>
                      </div>
                    }
                    @if (adjustmentAmount(adjustment.companyAdvanceAmount) > 0) {
                      <div
                        class="flex items-center justify-between gap-4 rounded-lg bg-sky-50 px-2.5 py-1.5"
                      >
                        <span class="text-xs text-sky-700">บริษัทสำรอง</span>
                        <span class="font-semibold whitespace-nowrap text-sky-700 tabular-nums">
                          {{ adjustment.companyAdvanceAmount | number: '1.2-2' }} บาท
                        </span>
                      </div>
                    }
                  </div>
                </td>
                <td class="px-4 py-4 text-center align-top">
                  @if (adjustment.advanceAttachmentPath) {
                    <a
                      [href]="passportUrl(adjustment.advanceAttachmentPath)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 hover:underline"
                      >เปิดไฟล์ <lucide-icon name="ExternalLink" [size]="13"
                    /></a>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
                <td class="px-4 py-4 text-center align-top">
                  <app-status-badge [status]="adjustment.status" />
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="6" class="px-4 py-10 text-center text-[13px] text-slate-500">
                  {{
                    isLoadingFinancialAdjustments()
                      ? 'กำลังโหลดรายการ...'
                      : 'ยังไม่มีรายการปรับวงเงินหรือขอเงินสำรอง'
                  }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">การชำระเงิน</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">สรุปยอดและประวัติการรับชำระของ Booking นี้</p>
      </div>
      @if (canManageBooking()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          style="font-size: 15px"
          [disabled]="remainingBalance() <= 0"
          (click)="openPaymentModal()"
        >
          <lucide-icon name="WalletCards" [size]="17" aria-hidden="true" />
          แจ้งรับชำระ
        </button>
      }
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[1100px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="min-w-52 px-4 py-3">รายการชำระ</th>
            <th class="w-48 px-4 py-3">รายละเอียดการชำระ</th>
            <th class="w-40 px-4 py-3">วันและเวลา</th>
            <th class="min-w-64 px-4 py-3">เอกสาร</th>
            <th class="min-w-52 px-4 py-3 text-right">ยอดชำระ</th>
            <th class="w-32 px-4 py-3 text-center">สถานะ</th>
            <th class="w-24 px-4 py-3 text-right">ดำเนินการ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (payment of payments(); track payment.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 font-semibold text-slate-800">{{ payment.payerName }}</p>
                <p class="mt-1 mb-0 text-xs font-medium text-sky-700">
                  {{ payment.receiptNumber }}
                </p>
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 font-medium text-slate-700">
                  {{ paymentStageLabel(payment.paymentStage) }}
                </p>
                <p class="mt-1 mb-0 text-xs text-slate-500">
                  {{ paymentMethodLabel(payment.paymentMethod) }}
                </p>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-slate-600 align-top">
                <p class="m-0">{{ payment.paidAt | date: 'dd MMM yyyy' }}</p>
                <p class="mt-1 mb-0 text-xs text-slate-500">
                  {{ payment.paidAt | date: 'HH:mm' }} น.
                </p>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex flex-wrap gap-x-3 gap-y-2 text-xs">
                  @if (payment.slipFileName) {
                    <a
                      [href]="passportUrl(payment.slipFileName)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >สลิป <lucide-icon name="ExternalLink" [size]="12"
                    /></a>
                  }
                  @if (payment.receiptUrl) {
                    <a
                      [href]="payment.receiptUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >ใบเสร็จ <lucide-icon name="ExternalLink" [size]="12"
                    /></a>
                  }
                  @if (payment.quotationUrl) {
                    <a
                      [href]="payment.quotationUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >ใบเสนอราคา <lucide-icon name="ExternalLink" [size]="12"
                    /></a>
                  }
                  @if (!payment.slipFileName && !payment.receiptUrl && !payment.quotationUrl) {
                    <span class="text-slate-400">—</span>
                  }
                </div>
              </td>
              <td class="px-4 py-4 text-right tabular-nums align-top">
                <p class="m-0 font-semibold whitespace-nowrap text-slate-800">
                  {{ payment.customerPaidAmount | number: '1.2-2' }} บาท
                </p>
                @if (payment.creditCardFeeAmount > 0) {
                  <p class="mt-1 mb-0 text-xs leading-5 text-slate-500">
                    ค่าธรรมเนียมบัตร
                    @if (payment.creditCardFeeResponsibility === 'CUSTOMER') {
                      (ลูกค้ารับภาระ)
                    } @else {
                      (บริษัทรับภาระ)
                    }
                    <span class="font-semibold text-amber-700">
                      {{ payment.creditCardFeeAmount | number: '1.2-2' }} บาท
                    </span>
                  </p>
                }
                @if (payment.withholdingTaxAmount > 0) {
                  <p class="mt-1 mb-0 text-xs leading-5 text-slate-500">
                    หัก ณ ที่จ่าย
                    <span class="font-semibold text-slate-700"
                      >-{{ payment.withholdingTaxAmount | number: '1.2-2' }} บาท</span
                    >
                  </p>
                }
              </td>
              <td class="px-4 py-4 text-center align-top">
                <app-status-badge [status]="payment.status" />
              </td>
              <td class="px-4 py-4 align-top">
                @if (canManageBooking()) {
                  <div class="flex justify-end gap-1.5">
                    @if (
                      payment.status !== 'WAITING_VERIFY' &&
                      payment.status !== 'VERIFIED' &&
                      payment.status !== 'REJECTED'
                    ) {
                      <button
                        type="button"
                        class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                        title="แก้ไข"
                        (click)="editPayment(payment)"
                      >
                        <lucide-icon name="Pencil" [size]="15" />
                      </button>
                    }
                    @if (payment.status !== 'WAITING_VERIFY' && payment.status !== 'VERIFIED') {
                      <button
                        type="button"
                        class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white p-0 text-red-600 hover:bg-red-50"
                        title="ลบ"
                        (click)="deletePayment(payment)"
                      >
                        <lucide-icon name="Trash2" [size]="15" />
                      </button>
                    }
                  </div>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="8" class="px-4 py-10 text-center text-[13px] text-slate-500">
                ยังไม่มีประวัติการชำระเงิน
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (canViewExpenseSection()) {
    <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div
      class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div>
        <h2 class="m-0 text-lg font-bold text-slate-800">รายการคืนเงิน</h2>
        <p class="mt-1 mb-0 text-sm text-slate-500">ประวัติและสถานะคำขอคืนเงินของ Booking นี้</p>
      </div>
      @if (canViewCreateRefundAction()) {
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
          [disabled]="!canCreateRefund()"
          [title]="refundButtonTitle()"
          (click)="openRefundModal()"
        >
          <lucide-icon name="Plus" [size]="17" aria-hidden="true" />
          คืนเงิน
        </button>
      }
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[1060px] border-collapse text-left text-[13px]">
        <thead
          class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
        >
          <tr>
            <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
            <th class="min-w-40 px-4 py-3">เลขที่คืนเงิน</th>
            <th class="w-36 px-4 py-3">ประเภท</th>
            <th class="min-w-52 px-4 py-3">ผู้รับเงินคืน</th>
            <th class="min-w-64 px-4 py-3">เหตุผล</th>
            <th class="w-48 px-4 py-3">เอกสาร</th>
            <th class="w-40 px-4 py-3">วันและเวลาโอน</th>
            <th class="w-44 px-4 py-3 text-right">จำนวนเงิน</th>
            <th class="w-36 px-4 py-3 text-center">สถานะ</th>
            <th class="w-24 px-4 py-3 text-right">ดำเนินการ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          @for (refund of refunds(); track refund.id; let index = $index) {
            <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
              <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap align-top">
                <p class="m-0 font-semibold text-sky-700">{{ refund.refundNo }}</p>
                <p class="mt-1 mb-0 text-xs font-normal text-slate-500">
                  {{ refund.createdAt | date: 'dd MMM yyyy, HH:mm' }} น.
                </p>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-slate-700 align-top">
                {{ refundTypeLabel(refund.refundType) }}
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 font-semibold text-slate-800">{{ refund.refundName }}</p>
                <p class="mt-1 mb-0 text-xs text-slate-500">
                  {{ refund.bankName }} · บัญชี {{ refund.accountNumber }}
                </p>
              </td>
              <td class="px-4 py-4 align-top">
                <p class="m-0 text-slate-700">{{ refund.reason }}</p>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex flex-wrap gap-x-3 gap-y-2 text-xs">
                  @if (refund.attachmentPath) {
                    <a
                      [href]="refundFileUrl(refund.attachmentPath)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >เอกสารคำขอ <lucide-icon name="ExternalLink" [size]="12"
                    /></a>
                  }
                  @if (refund.transferSlipUrl) {
                    <a
                      [href]="refundFileUrl(refund.transferSlipUrl)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                      >สลิปโอนเงิน <lucide-icon name="ExternalLink" [size]="12"
                    /></a>
                  }
                  @if (!refund.attachmentPath && !refund.transferSlipUrl) {
                    <span class="text-slate-400">—</span>
                  }
                </div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-slate-600 align-top">
                @if (refund.transferredAt) {
                  <p class="m-0">{{ refund.transferredAt | date: 'dd MMM yyyy' }}</p>
                  <p class="mt-1 mb-0 text-xs text-slate-500">
                    {{ refund.transferredAt | date: 'HH:mm' }} น.
                  </p>
                } @else {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-4 py-4 text-right font-semibold whitespace-nowrap text-slate-800 tabular-nums align-top">
                {{ refund.amount | number: '1.2-2' }} บาท
              </td>
              <td class="px-4 py-4 text-center align-top">
                <app-status-badge [status]="refund.status" />
              </td>
              <td class="px-4 py-4 text-right align-top">
                @if (canDeleteRejectedBookingRecord() && refund.status === 'REJECTED') {
                  <div class="flex justify-end gap-1.5">
                    <button
                      type="button"
                      class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white p-0 text-red-600 hover:bg-red-50"
                      title="ลบ"
                      aria-label="ลบรายการคืนเงิน"
                      (click)="deleteRefund(refund)"
                    >
                      <lucide-icon name="Trash2" [size]="15" />
                    </button>
                  </div>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="10" class="px-4 py-10 text-center text-[13px] text-slate-500">
                ยังไม่มีรายการคืนเงิน
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    </section>
  }

  @if (canViewExpenseSection() && !isInHouseTour()) {
    <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
      >
        <div>
          <h2 class="m-0 text-lg font-bold text-slate-800">เบิกจ่าย</h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">รายการขอเบิกค่าใช้จ่ายของ Booking นี้</p>
        </div>
        @if (canManageBooking()) {
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
            [disabled]="!canCreateExpense()"
            [title]="
              hasBlockingCommissionForExpense()
                ? booking.bookingType === 'JOIN_TOUR'
                  ? 'รายการจองนี้มีค่าคอมมิชชั่นที่ตรวจสอบแล้ว'
                  : 'รายการจองนี้มีค่าคอมมิชชั่นแล้ว'
                : availableExpenseAmount() <= 0
                  ? 'ยังไม่มียอดชำระหรือวงเงินสำรองที่อนุมัติ หรือวงเงินเบิกคงเหลือไม่เพียงพอ'
                  : 'สร้างใบขอเบิก'
            "
            (click)="openExpenseModal()"
          >
            <lucide-icon name="FileText" [size]="17" aria-hidden="true" /> สร้างใบขอเบิก
          </button>
        }
      </div>
      <div class="overflow-x-auto">
        <table class="w-full min-w-[980px] border-collapse text-left text-[13px]">
          <thead
            class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
          >
            <tr>
              <th class="w-14 px-4 py-3 text-center">ลำดับ</th>
              <th class="w-52 px-4 py-3">รายการเบิก</th>
              <th class="min-w-64 px-4 py-3">รายละเอียด</th>
              <th class="w-48 px-4 py-3">เอกสาร</th>
              <th class="w-44 px-4 py-3 text-right">จำนวนเงิน</th>
              <th class="w-32 px-4 py-3 text-center">สถานะ</th>
              <th class="w-28 px-4 py-3 text-right">ดำเนินการ</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            @for (expense of expenses(); track expense.id; let index = $index) {
              <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
                <td class="px-4 py-4 text-center text-slate-400 tabular-nums align-top">
                  {{ index + 1 }}
                </td>
                <td class="px-4 py-4 align-top">
                  <p class="m-0 font-semibold whitespace-nowrap text-sky-700">{{ expense.code }}</p>
                  <p class="mt-1 mb-0 text-xs text-slate-600">{{ expense.expenseNameSnapshot }}</p>
                  @if (expense.wholesaleNameSnapshot) {
                    <p class="mt-1 mb-0 text-xs text-slate-400">
                      โฮลเซล: {{ expense.wholesaleNameSnapshot }}
                    </p>
                  }
                </td>
                <td class="px-4 py-4 align-top">
                  <p class="m-0 leading-5 font-medium text-slate-700">{{ expense.description }}</p>
                  <p class="mt-1.5 mb-0 text-xs text-slate-500">ผู้ขอ: {{ expense.requester }}</p>
                </td>
                <td class="px-4 py-4 align-top">
                  <div class="flex flex-wrap gap-x-3 gap-y-2 text-xs">
                    @if (expense.purchaseOrderFileName) {
                      <a
                        [href]="passportUrl(expense.purchaseOrderFileName)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                        >ใบสั่งซื้อ <lucide-icon name="ExternalLink" [size]="12"
                      /></a>
                    }
                    @if (expense.receiptFileName) {
                      <a
                        [href]="passportUrl(expense.receiptFileName)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                        >ใบเสร็จ <lucide-icon name="ExternalLink" [size]="12"
                      /></a>
                    }
                    @if (expense.transferSlipUrl) {
                      <a
                        [href]="expense.transferSlipUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-sky-700 no-underline hover:underline"
                        >สลิปโอนจ่าย <lucide-icon name="ExternalLink" [size]="12"
                      /></a>
                    }
                    @if (
                      !expense.purchaseOrderFileName &&
                      !expense.receiptFileName &&
                      !expense.transferSlipUrl
                    ) {
                      <span class="text-slate-400">—</span>
                    }
                  </div>
                </td>
                <td class="px-4 py-4 text-right align-top">
                  @if (expense.actualTransferAmount !== null) {
                    <p class="m-0 font-semibold whitespace-nowrap text-slate-800 tabular-nums">
                      {{ expense.actualTransferAmount | number: '1.2-2' }} บาท
                    </p>
                    @if (expense.amount !== null) {
                      <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500 tabular-nums">
                        ประมาณ {{ expense.amount | number: '1.2-2' }} บาท
                      </p>
                    }
                  } @else {
                    <p class="m-0 text-xs text-slate-400">ยังไม่ระบุยอดจริง</p>
                    @if (expense.amount !== null) {
                      <p
                        class="mt-1 mb-0 font-medium whitespace-nowrap text-slate-600 tabular-nums"
                      >
                        ประมาณ {{ expense.amount | number: '1.2-2' }} บาท
                      </p>
                    }
                  }
                </td>
                <td class="px-4 py-4 text-center align-top">
                  <app-status-badge [status]="expense.status" />
                </td>
                <td class="px-4 py-4 text-right align-top">
                  <div class="flex items-center justify-end gap-1.5">
                    @if (canManageBooking() && expense.status === 'DRAFT') {
                      <button
                        type="button"
                        class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                        title="แก้ไขใบขอเบิก"
                        aria-label="แก้ไขใบขอเบิก"
                        (click)="editExpense(expense)"
                      >
                        <lucide-icon name="Pencil" [size]="15" />
                      </button>
                    }
                    @if (
                      canManageBooking() &&
                      expense.status === 'WAITING_CLEARING' &&
                      !expense.receiptFileName
                    ) {
                      <button
                        type="button"
                        class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-sky-200 bg-white text-sky-700 transition hover:bg-sky-50"
                        title="ส่งใบเสร็จ"
                        aria-label="ส่งใบเสร็จ"
                        (click)="openExpenseReceiptModal(expense)"
                      >
                        <lucide-icon name="Upload" [size]="15" />
                      </button>
                    }
                    @if (
                      canManageBooking() &&
                      expense.source !== 'BOOKING_ESTIMATE' &&
                      (expense.status === 'DRAFT' || expense.status === 'REJECTED')
                    ) {
                      <button
                        type="button"
                        class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50"
                        title="ลบใบขอเบิก"
                        aria-label="ลบใบขอเบิก"
                        (click)="deleteExpense(expense)"
                      >
                        <lucide-icon name="Trash2" [size]="15" />
                      </button>
                    }
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="7" class="px-4 py-10 text-center text-[13px] text-slate-500">
                  ยังไม่มีรายการเบิกจ่ายใน Booking นี้
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  @if (booking.bookingType !== 'GROUP_TOUR') {
    <section class="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div
        class="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
      >
        <div>
          <h2 class="m-0 text-lg font-bold text-slate-800">ค่าคอมมิชชั่น</h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            รายการค่าคอมมิชชั่นที่เกี่ยวข้องกับรายการจองนี้
          </p>
        </div>
        @if (canRequestCommission()) {
          <div class="flex flex-wrap gap-2 self-start sm:self-auto">
            @if (canViewCommissionButton('SALE')) {
              <button
                type="button"
                class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-4 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[var(--theme-hover)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                [disabled]="!canCreateCommissionFor('SALE')"
                [title]="commissionButtonTitle('SALE')"
                (click)="openCommissionModal('SALE')"
              >
                <lucide-icon name="CircleDollarSign" [size]="17" aria-hidden="true" />
                เบิกพนักงานขาย
              </button>
            }
            @if (canViewCommissionButton('CARETAKER') && !booking.tourId) {
              <button
                type="button"
                class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 text-[15px] font-semibold text-sky-700 shadow-sm transition hover:bg-sky-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                [disabled]="!canCreateCommissionFor('CARETAKER')"
                [title]="commissionButtonTitle('CARETAKER')"
                (click)="openCommissionModal('CARETAKER')"
              >
                <lucide-icon name="UserRoundCheck" [size]="17" aria-hidden="true" /> เบิกผู้ดูแล
              </button>
            }
          </div>
        }
      </div>
      <div class="overflow-x-auto">
        <table class="w-full min-w-[1520px] border-collapse text-left text-[13px]">
          <thead
            class="bg-slate-50 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500"
          >
            <tr>
              <th class="w-16 px-5 py-3 text-center">ลำดับ</th>
              <th class="min-w-48 px-5 py-3">วันที่และเลขค่าคอม</th>
              <th class="min-w-40 px-5 py-3">ประเภท</th>
              <th class="min-w-60 px-5 py-3">ผู้รับ / ผู้เบิก</th>
              <th class="min-w-64 px-5 py-3">อัตราค่าคอม</th>
              <th class="min-w-44 px-5 py-3 text-right">ยอดค่าคอม</th>
              <th class="min-w-48 px-5 py-3 text-right">ยอดหักคืน</th>
              <th class="w-28 px-5 py-3 text-center">เอกสาร</th>
              <th class="min-w-36 px-5 py-3 text-center">สถานะ</th>
              <th class="w-24 px-5 py-3 text-center">ดำเนินการ</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            @for (commission of commissions(); track commission.id; let index = $index) {
              <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/50">
                <td class="px-5 py-3.5 text-center text-slate-400 tabular-nums align-top">
                  {{ index + 1 }}
                </td>
                <td class="px-5 py-3.5 align-top">
                  <p class="m-0 font-medium whitespace-nowrap text-slate-700">
                    {{ (commission.submittedAt || commission.createdAt) | date: 'dd/MM/yyyy' }}
                  </p>
                  <p class="mt-1 mb-0 font-semibold whitespace-nowrap text-sky-700">
                    {{ commission.code }}
                  </p>
                </td>

                <td class="px-5 py-3.5 align-top">
                  <p class="m-0 font-medium text-slate-700">
                    {{
                      commission.recipientRole === 'SALE'
                        ? 'พนักงานขาย'
                        : 'ผู้ดูแล'
                    }}
                  </p>
                  <p class="mt-1 mb-0 text-xs text-slate-500">
                    {{ commission.pax | number }} คน
                  </p>
                </td>

                <td class="px-5 py-3.5 align-top">
                  <p class="m-0 font-semibold leading-5 text-slate-800">
                    {{ commission.recipientName }}
                  </p>
                  <p class="mt-1 mb-0 text-xs leading-5 text-slate-500">
                    ผู้เบิก: {{ commission.requesterName }}
                  </p>
                </td>

                <td class="px-5 py-3.5 align-top">
                  @if (commission.recipientRole === 'SALE') {
                    <p class="m-0 font-semibold whitespace-nowrap text-slate-700 tabular-nums">
                      {{
                        commission.saleCommission + commission.specialCommission
                          | number: '1.2-2'
                      }}
                      บาท
                    </p>
                    <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500 tabular-nums">
                      Sale {{ commission.saleCommission | number: '1.2-2' }} + พิเศษ
                      {{ commission.specialCommission | number: '1.2-2' }}
                    </p>
                  } @else {
                    <p class="m-0 font-semibold whitespace-nowrap text-slate-700 tabular-nums">
                      {{ commission.specialCommissionRate | number: '1.2-2' }} บาท/คน
                    </p>
                    <p class="mt-1 mb-0 text-xs text-slate-500">ผู้ดูแล × {{ commission.pax | number }} คน</p>
                  }
                </td>

                <td class="px-5 py-3.5 text-right align-top">
                  <p class="m-0 font-bold whitespace-nowrap text-sky-700 tabular-nums">
                    {{ commission.total | number: '1.2-2' }} บาท
                  </p>
                  @if (commission.status === 'VERIFIED') {
                    <p class="mt-1 mb-0 text-xs whitespace-nowrap text-emerald-700 tabular-nums">
                      ได้รับจริง {{ commission.paidAmount | number: '1.2-2' }} บาท
                    </p>
                  }
                </td>

                <td class="px-5 py-3.5 text-right align-top">
                  @if (commission.recoveries.length > 0) {
                    <p class="m-0 font-semibold whitespace-nowrap text-red-600 tabular-nums">
                      {{ commissionRecoveryTotal(commission, 'recoveryAmount') | number: '1.2-2' }} บาท
                    </p>
                    <p class="mt-1 mb-0 text-xs whitespace-nowrap text-amber-700 tabular-nums">
                      คงเหลือ
                      {{ commissionRecoveryTotal(commission, 'remainingAmount') | number: '1.2-2' }} บาท
                    </p>
                    <button
                      type="button"
                      class="tw-button mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800 hover:underline"
                      (click)="openCommissionRecoveryHistory(commission)"
                    >
                      <lucide-icon name="History" [size]="13" aria-hidden="true" /> ดูประวัติ
                    </button>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>

                <td class="px-5 py-3.5 text-center align-top">
                  @if (commission.attachmentFileName) {
                    <a
                      [href]="passportUrl(commission.attachmentFileName)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1.5 font-semibold whitespace-nowrap text-sky-700 hover:text-sky-800 hover:underline"
                    >
                      <lucide-icon name="Paperclip" [size]="14" /> เอกสาร
                    </a>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>

                <td class="px-5 py-3.5 text-center align-top">
                  <app-status-badge [status]="commission.status" />
                </td>

                <td class="px-5 py-3.5 text-center align-top">
                  @if (
                    (commission.status === 'DRAFT' && canManageBooking()) ||
                    (commission.status === 'REJECTED' && canDeleteRejectedBookingRecord())
                  ) {
                    <div class="flex items-center justify-center gap-2">
                      @if (commission.status === 'DRAFT') {
                        <button
                          type="button"
                          class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
                          title="ส่งเบิกค่าคอมมิชชั่น"
                          [disabled]="
                            !isCommissionPaymentComplete() ||
                            !isSaleCommissionBookingConfirmed(commission.recipientRole)
                          "
                          [class.opacity-50]="
                            !isCommissionPaymentComplete() ||
                            !isSaleCommissionBookingConfirmed(commission.recipientRole)
                          "
                          [class.cursor-not-allowed]="
                            !isCommissionPaymentComplete() ||
                            !isSaleCommissionBookingConfirmed(commission.recipientRole)
                          "
                          (click)="editCommission(commission)"
                        >
                          <lucide-icon name="Send" [size]="15" />
                        </button>
                      }
                      @if (commission.status === 'REJECTED') {
                        <button
                          type="button"
                          class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-red-200 bg-white p-0 text-red-600 hover:bg-red-50"
                          title="ลบ"
                          (click)="deleteCommission(commission)"
                        >
                          <lucide-icon name="Trash2" [size]="15" />
                        </button>
                      }
                    </div>
                  } @else {
                    <span class="text-slate-400">—</span>
                  }
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="10" class="px-5 py-12 text-center text-[13px] text-slate-500">
                  ยังไม่มีรายการค่าคอมมิชชั่นในรายการจองนี้
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  @if (selectedCommissionRecovery(); as commission) {
    <div
      class="fixed inset-0 z-[230] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeCommissionRecoveryHistory()"
      (keydown.escape)="closeCommissionRecoveryHistory()"
    >
      <section
        class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-recovery-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-red-600 uppercase">ประวัติยอดหักคืน</p>
            <h2 id="commission-recovery-title" class="m-0 text-xl font-bold text-slate-800">
              {{ commission.code }} · {{ commission.recipientName }}
            </h2>
            <p class="mt-1 mb-0 text-sm text-slate-500">ยอดหักคืนจากการคืนเงินของ Booking นี้</p>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeCommissionRecoveryHistory()"
          >
            <lucide-icon name="X" [size]="19" aria-hidden="true" />
          </button>
        </div>

        <div class="grid gap-3 border-b border-slate-100 bg-slate-50 px-5 py-4 sm:grid-cols-3 sm:px-6">
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดต้องหักคืน</p>
            <p class="mt-1 mb-0 text-lg font-bold text-red-600 tabular-nums">
              {{ commissionRecoveryTotal(commission, 'recoveryAmount') | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">หักคืนแล้ว</p>
            <p class="mt-1 mb-0 text-lg font-bold text-emerald-700 tabular-nums">
              {{ commissionRecoveryTotal(commission, 'settledAmount') | number: '1.2-2' }} บาท
            </p>
          </div>
          <div>
            <p class="m-0 text-xs font-medium text-slate-500">ยอดคงเหลือ</p>
            <p class="mt-1 mb-0 text-lg font-bold text-red-600 tabular-nums">
              {{ commissionRecoveryTotal(commission, 'remainingAmount') | number: '1.2-2' }} บาท
            </p>
          </div>
        </div>

        <div class="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-5 sm:px-6">
          @for (recovery of commission.recoveries; track recovery.id) {
            <article class="rounded-xl border border-slate-200 bg-white p-4">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="m-0 font-semibold text-slate-800">{{ recovery.refundNo }}</p>
                    <span
                      class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                      [class]="commissionRecoveryStatusClass(recovery.status)"
                    >{{ commissionRecoveryStatusLabel(recovery.status) }}</span>
                  </div>
                  <p class="mt-1 mb-0 text-xs text-slate-500">
                    {{ recovery.refundType === 'FULL' ? 'คืนเงินเต็มจำนวน' : 'คืนเงินบางส่วน' }}
                    · {{ recovery.createdAt | date: 'dd MMM yyyy, HH:mm' }}
                  </p>
                  <p class="mt-2 mb-0 text-sm text-slate-600">{{ recovery.reason }}</p>
                </div>
                <div class="grid grid-cols-3 gap-x-4 text-right tabular-nums">
                  <div>
                    <p class="m-0 text-[11px] text-slate-500">ต้องหัก</p>
                    <p class="mt-1 mb-0 font-semibold text-red-600">{{ recovery.recoveryAmount | number: '1.2-2' }}</p>
                  </div>
                  <div>
                    <p class="m-0 text-[11px] text-slate-500">หักแล้ว</p>
                    <p class="mt-1 mb-0 font-semibold text-emerald-700">{{ recovery.settledAmount | number: '1.2-2' }}</p>
                  </div>
                  <div>
                    <p class="m-0 text-[11px] text-slate-500">คงเหลือ</p>
                    <p class="mt-1 mb-0 font-semibold text-red-600">{{ recovery.remainingAmount | number: '1.2-2' }}</p>
                  </div>
                </div>
              </div>

              <div class="mt-4 border-t border-slate-100 pt-3">
                <p class="m-0 text-xs font-semibold text-slate-600">ประวัติการนำยอดไปหัก</p>
                @for (application of recovery.applications; track application.id) {
                  <div class="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                    <span class="font-medium text-slate-700">{{ application.targetCode }}</span>
                    <span class="text-slate-500">{{ commissionRecoveryApplicationStatusLabel(application.status) }}</span>
                    <span class="font-semibold text-slate-800 tabular-nums">{{ application.amount | number: '1.2-2' }} บาท</span>
                  </div>
                } @empty {
                  <p class="mt-2 mb-0 text-xs text-slate-500">ยังไม่มีการนำยอดนี้ไปหักกับคอมมิชชั่นรอบใหม่</p>
                }
              </div>
            </article>
          }
        </div>
        <div class="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
          <button
            type="button"
            class="tw-button h-10 rounded-xl border border-slate-300 bg-white px-4 font-semibold text-slate-700 hover:bg-slate-100"
            (click)="closeCommissionRecoveryHistory()"
          >ปิด</button>
        </div>
      </section>
    </div>
  }

  @if (isFinancialAdjustmentModalOpen() && canCreateFinancialAdjustment()) {
    <div
      class="fixed inset-0 z-[220] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeFinancialAdjustmentModal()"
    >
      <section
        class="my-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="financial-adjustment-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              จัดการวงเงิน Booking
            </p>
            <h2 id="financial-adjustment-title" class="m-0 text-xl font-bold text-slate-800">
              ปรับวงเงินและขอสำรองจ่าย
            </h2>
            <p class="mt-1 mb-0 text-sm text-slate-500">
              {{ booking.bookingNo }} · กรอกเฉพาะยอดที่ต้องการเพิ่ม
            </p>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeFinancialAdjustmentModal()"
          >
            <lucide-icon name="X" [size]="19" />
          </button>
        </div>

        <form [formGroup]="financialAdjustmentForm" (ngSubmit)="saveFinancialAdjustment()">
          <div class="grid gap-5 px-5 py-5 sm:px-6">
            <div class="rounded-xl border border-sky-100 bg-sky-50/70 p-4 text-sm text-slate-600">
              <div class="flex gap-3">
                <lucide-icon name="CircleHelp" [size]="20" class="mt-0.5 shrink-0 text-sky-600" />
                <p class="m-0 leading-6">
                  เพิ่มวงเงินค่าใช้จ่ายและยอดเรียกเก็บได้อย่างใดอย่างหนึ่งหรือพร้อมกัน
                  หากบริษัทต้องออกเงินก่อน ให้กรอกยอดสำรองและแนบเอกสารประกอบ
                </p>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>เพิ่มวงเงินค่าใช้จ่าย</span>
                <p-inputnumber
                  class="w-full font-normal"
                  inputStyleClass="h-11 w-full"
                  formControlName="expenseBudgetDelta"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="0"
                  [max]="expenseBudgetMaxInput()"
                  placeholder="0.00"
                />
                <small class="font-normal leading-5 text-slate-500"
                  >เพิ่มเพดานสำหรับสร้างใบเบิก</small
                >
                @if (isExpenseBudgetOverLimit()) {
                  <small class="font-normal leading-5 text-red-600">
                    เพิ่มวงเงินได้ไม่เกิน
                    {{ expenseBudgetIncreaseLimit() | number: '1.2-2' }} บาท
                  </small>
                } @else if (hasExpenseBudgetIncrease()) {
                  <small class="font-normal leading-5 text-slate-500">
                    เพิ่มได้อีกไม่เกิน
                    {{ expenseBudgetIncreaseLimit() | number: '1.2-2' }} บาท
                  </small>
                }
              </label>

              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>เพิ่มยอดเรียกเก็บลูกค้า</span>
                <p-inputnumber
                  class="w-full font-normal"
                  inputStyleClass="h-11 w-full"
                  formControlName="customerChargeDelta"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="0"
                  placeholder="0.00"
                />
                <small class="font-normal leading-5 text-slate-500"
                  >เพิ่มยอดที่ลูกค้าต้องชำระ</small
                >
              </label>

              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>บริษัทสำรองจ่ายก่อน</span>
                <p-inputnumber
                  class="w-full font-normal"
                  inputStyleClass="h-11 w-full"
                  formControlName="companyAdvanceAmount"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                  [min]="0"
                  placeholder="0.00"
                />
                <small class="font-normal leading-5 text-slate-500"
                  >ใช้เมื่อเงินรับชำระยังไม่เพียงพอ</small
                >
              </label>
            </div>

            @if (financialAdjustmentSubmitted() && !hasFinancialAdjustmentAmount()) {
              <p class="m-0 text-sm font-medium text-red-600">
                กรุณากรอกจำนวนเงินอย่างน้อยหนึ่งรายการให้มากกว่า 0 บาท
              </p>
            }

            <div
              class="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3"
            >
              <div>
                <p class="m-0 text-xs text-slate-500">วงเงินค่าใช้จ่ายหลังปรับ</p>
                <strong class="mt-1 block text-base text-slate-800">
                  {{ projectedExpenseBudget() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div>
                <p class="m-0 text-xs text-slate-500">ยอดเรียกเก็บหลังปรับ</p>
                <strong class="mt-1 block text-base text-slate-800">
                  {{ projectedCustomerChargeTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
              <div>
                <p class="m-0 text-xs text-slate-500">ยอดบริษัทสำรอง</p>
                <strong class="mt-1 block text-base text-slate-800">
                  {{ projectedCompanyAdvanceTotal() | number: '1.2-2' }} บาท
                </strong>
              </div>
            </div>

            @if (hasCompanyAdvanceRequest()) {
              <div class="grid w-full content-start gap-1 text-sm font-semibold text-slate-700">
                <span>เอกสารประกอบการขอสำรองจ่าย <span class="text-red-500">*</span></span>
                <div
                  class="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4"
                >
                  <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <p-fileupload
                      #advanceUpload
                      mode="basic"
                      name="advanceAttachment"
                      chooseLabel="เลือกไฟล์"
                      accept=".pdf,.jpg,.jpeg,.png"
                      [maxFileSize]="5000000"
                      [auto]="false"
                      (onSelect)="selectAdvanceAttachment($event)"
                      (onClear)="advanceAttachmentFile.set(null); advanceAttachmentFileName.set('')"
                    />
                    <button
                      type="button"
                      class="tw-button inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-semibold text-red-600 disabled:opacity-40"
                      [disabled]="!advanceUpload.files.length"
                      (click)="
                        advanceUpload.clear();
                        advanceAttachmentFile.set(null);
                        advanceAttachmentFileName.set('')
                      "
                    >
                      <lucide-icon name="X" [size]="16" /> ล้างไฟล์
                    </button>
                  </div>
                  @if (advanceAttachmentFileName()) {
                    <p class="mt-2 mb-0 break-all text-xs font-normal text-sky-700">
                      {{ advanceAttachmentFileName() }}
                    </p>
                  }
                  <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                    รองรับ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                  </p>
                </div>
                <span class="min-h-4">
                  @if (financialAdjustmentSubmitted() && !advanceAttachmentFileName()) {
                    <small class="font-normal text-red-600">กรุณาแนบเอกสารประกอบ</small>
                  }
                </span>
              </div>
            }

            <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
              <span>เหตุผลในการขอปรับยอด <span class="text-red-500">*</span></span>
              <textarea
                class="min-h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                formControlName="reason"
                maxlength="500"
                placeholder="ระบุรายละเอียดและเหตุผลที่ต้องเพิ่มวงเงิน เรียกเก็บเพิ่ม หรือสำรองจ่าย"
              ></textarea>
              <span class="min-h-4">
                @if (isFinancialAdjustmentFieldInvalid('reason')) {
                  <small class="font-normal text-red-600">กรุณาระบุเหตุผลในการขอปรับยอด</small>
                }
              </span>
            </label>
          </div>

          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closeFinancialAdjustmentModal()"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)]"
              [disabled]="isSavingFinancialAdjustment() || isExpenseBudgetOverLimit()"
            >
              <lucide-icon name="Send" [size]="18" aria-hidden="true" />
              {{ isSavingFinancialAdjustment() ? 'กำลังบันทึก...' : 'บันทึกคำขอ' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isExpenseModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeExpenseModal()"
    >
      <section
        class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-expense-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              ข้อมูลค่าใช้จ่าย
            </p>
            <h2 id="booking-expense-title" class="m-0 text-xl font-bold text-slate-800">
              {{ editingExpense() ? 'แก้ไขใบขอเบิก' : 'สร้างใบขอเบิก' }}
            </h2>
            <p class="mt-1 mb-0 text-sm text-slate-500">{{ booking.bookingNo }}</p>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeExpenseModal()"
          >
            <lucide-icon name="X" [size]="19" />
          </button>
        </div>
        <form [formGroup]="expenseForm" (ngSubmit)="saveExpense(false)">
          <div class="grid gap-x-6 gap-y-0 px-5 py-5 sm:grid-cols-2 sm:px-6">
            @if (booking.bookingType === 'JOIN_TOUR') {
              <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
                <span>ประเภทค่าใช้จ่าย</span>
                <input
                  pInputText
                  class="h-11 w-full bg-slate-100 font-normal text-slate-600"
                  [value]="expenseForm.controls.expenseNameSnapshot.value"
                  [disabled]="true"
                  readonly
                />
                <span class="min-h-4"></span>
              </label>
              <label class="grid content-start gap-1 text-sm font-semibold text-slate-700">
                <span>รายการโฮลเซล</span>
                <input
                  pInputText
                  class="h-11 w-full bg-slate-100 font-normal text-slate-600"
                  [value]="editingExpense()?.wholesaleNameSnapshot || booking.wholesaleName"
                  [disabled]="true"
                  readonly
                />
                <span class="min-h-4">
                  @if (isExpenseFieldInvalid('wholesaleId')) {
                    <small class="font-normal text-red-600">ไม่พบรายการโฮลเซลของ Booking</small>
                  }
                </span>
              </label>
            } @else {
              <label
                class="grid content-start gap-1 text-sm font-semibold text-slate-700"
                [class.sm:col-span-2]="!editingExpense()?.wholesaleId"
                ><span>ประเภทค่าใช้จ่าย <span class="text-red-500">*</span></span
                ><p-select
                  class="h-11 w-full font-normal"
                  formControlName="expenseNameSnapshot"
                  [options]="expenseTypeOptions"
                  placeholder="เลือกประเภทค่าใช้จ่าย"
                /><span class="min-h-4">
                  @if (isExpenseFieldInvalid('expenseNameSnapshot')) {
                    <small class="font-normal text-red-600">กรุณาเลือกประเภทค่าใช้จ่าย</small>
                  }
                </span></label
              >
              @if (editingExpense()?.wholesaleId) {
                <label class="grid content-start gap-1 text-sm font-semibold text-slate-700"
                  ><span>รายการโฮลเซล <span class="text-red-500">*</span></span
                  ><p-select
                    class="h-11 w-full font-normal"
                    formControlName="wholesaleId"
                    [options]="wholesaleOptions()"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="เลือกรายการโฮลเซล"
                    [filter]="true"
                    filterBy="label"
                    appendTo="body"
                  /><span class="min-h-4">
                    @if (isExpenseFieldInvalid('wholesaleId')) {
                      <small class="font-normal text-red-600">กรุณาเลือกรายการโฮลเซล</small>
                    }
                  </span></label
                >
              }
            }
            @if (editingExpense()?.source === 'BOOKING_ESTIMATE') {
              <label class="grid content-start gap-1 text-sm font-semibold text-slate-700"
                ><span>ยอดโดยประมาณ</span
                ><p-inputnumber
                  class="w-full font-normal"
                  inputStyleClass="h-11 w-full"
                  formControlName="amount"
                  mode="currency"
                  currency="THB"
                  locale="th-TH"
                /><span class="min-h-4"></span>
              </label>
            }
            <label
              class="grid content-start gap-1 text-sm font-semibold text-slate-700"
              [class.sm:col-span-2]="editingExpense()?.source !== 'BOOKING_ESTIMATE'"
              ><span>ยอดเงินจริง <span class="text-red-500">*</span></span
              ><p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full"
                formControlName="actualTransferAmount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                [min]="1"
                [max]="expenseActualTransferLimit()"
                placeholder="0.00"
                (onInput)="checkExpenseAmount($event.value)"
              /><span class="min-h-4">
                @if (isExpenseFieldInvalid('actualTransferAmount')) {
                  @if (expenseForm.controls.actualTransferAmount.hasError('max')) {
                    <small class="font-normal text-red-600">
                      ยอดที่ต้องโอนจริงต้องไม่เกิน
                      {{ expenseActualTransferLimit() | number: '1.2-2' }} บาท
                    </small>
                  } @else {
                    <small class="font-normal text-red-600"
                      >กรุณากรอกยอดที่ต้องโอนจริงมากกว่า 0</small
                    >
                  }
                }
              </span>
            </label>
            <label
              class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2"
              ><span>รายละเอียด <span class="text-red-500">*</span></span
              ><input
                pInputText
                class="h-11 w-full font-normal"
                formControlName="description"
                placeholder="ระบุรายละเอียดที่ต้องการขอเบิก"
              /><span class="min-h-4">
                @if (isExpenseFieldInvalid('description')) {
                  <small class="font-normal text-red-600">กรุณากรอกรายละเอียด</small>
                }
              </span></label
            >
            <div
              class="grid content-start gap-1 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>แนบใบสั่งซื้อ <span class="text-red-500">*</span></span>
              <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #expenseUpload
                    mode="basic"
                    name="purchaseOrder"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="5000000"
                    [auto]="false"
                    (onSelect)="selectPurchaseOrder($event)"
                    (onClear)="purchaseOrderFile.set(null); purchaseOrderFileName.set('')"
                  /><button
                    type="button"
                    class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!expenseUpload.files.length"
                    (click)="
                      expenseUpload.clear();
                      purchaseOrderFile.set(null);
                      purchaseOrderFileName.set('')
                    "
                  >
                    <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                  </button>
                </div>
                @if (purchaseOrderFileName()) {
                  <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                    {{ purchaseOrderFileName() }}
                  </p>
                }
                @if (editingExpense()?.purchaseOrderFileName; as oldFile) {
                  <a
                    [href]="passportUrl(oldFile)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-3 text-xs font-semibold text-sky-700 no-underline transition hover:bg-sky-50 hover:text-sky-900"
                    ><lucide-icon name="ExternalLink" [size]="15" aria-hidden="true" />ดูไฟล์เดิม</a
                  >
                }
                <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                  รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                </p>
              </div>
              @if (!purchaseOrderFileName() && expenseForm.touched) {
                <small class="font-normal text-red-600">กรุณาแนบไฟล์ใบสั่งซื้อ</small>
              }
            </div>
          </div>
          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closeExpenseModal()"
            >
              ยกเลิก</button
            ><button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              [disabled]="
                (!editingExpense() && !canCreateExpense()) ||
                expenseForm.invalid ||
                !purchaseOrderFileName() ||
                isSavingExpense()
              "
            >
              <lucide-icon name="Check" [size]="18" />
              {{ isSavingExpense() ? 'กำลังบันทึก...' : 'บันทึกร่าง' }}
            </button>
            <button
              type="button"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:opacity-50"
              [disabled]="
                (!editingExpense() && !canCreateExpense()) ||
                expenseForm.invalid ||
                !purchaseOrderFileName() ||
                isSavingExpense()
              "
              (click)="saveExpense(true)"
            >
              <lucide-icon name="Send" [size]="18" />
              {{ isSavingExpense() ? 'กำลังส่ง...' : 'ส่งเบิก' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (!isBookingDeleted() && selectedReceiptExpense(); as expense) {
    <div
      class="fixed inset-0 z-[210] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeExpenseReceiptModal()"
    >
      <section
        class="my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="expense-receipt-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              {{ expense.code }}
            </p>
            <h2 id="expense-receipt-title" class="m-0 text-xl font-bold text-slate-800">
              ส่งใบเสร็จ
            </h2>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            [disabled]="isSavingExpenseReceipt()"
            (click)="closeExpenseReceiptModal()"
          >
            <lucide-icon name="X" [size]="19" />
          </button>
        </div>

        <div class="p-5 sm:p-6">
          <div class="grid gap-1 text-sm font-semibold text-slate-700">
            <span>แนบไฟล์ใบเสร็จ <span class="text-red-500">*</span></span>
            <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <p-fileupload
                  #receiptUpload
                  mode="basic"
                  name="expenseReceipt"
                  chooseLabel="เลือกไฟล์"
                  accept=".pdf,.jpg,.jpeg,.png"
                  [maxFileSize]="5000000"
                  [auto]="false"
                  (onSelect)="selectExpenseReceipt($event)"
                  (onClear)="expenseReceiptFile.set(null); expenseReceiptFileName.set('')"
                />
                <button
                  type="button"
                  class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                  [disabled]="!receiptUpload.files.length || isSavingExpenseReceipt()"
                  (click)="
                    receiptUpload.clear();
                    expenseReceiptFile.set(null);
                    expenseReceiptFileName.set('')
                  "
                >
                  <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                </button>
              </div>
              @if (expenseReceiptFileName()) {
                <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                  {{ expenseReceiptFileName() }}
                </p>
              }
              <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
              </p>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
        >
          <button
            type="button"
            class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
            [disabled]="isSavingExpenseReceipt()"
            (click)="closeExpenseReceiptModal()"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            [disabled]="!expenseReceiptFile() || isSavingExpenseReceipt()"
            (click)="saveExpenseReceipt()"
          >
            <lucide-icon name="Check" [size]="18" />
            {{ isSavingExpenseReceipt() ? 'กำลังบันทึก...' : 'บันทึกใบเสร็จ' }}
          </button>
        </div>
      </section>
    </div>
  }

  @if (isCommissionModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeCommissionModal()"
    >
      <section
        class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-commission-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              คำขอเบิกค่าคอมมิชชั่น
            </p>
            <h2 id="booking-commission-title" class="m-0 text-xl font-bold text-slate-800">
              {{
                commissionRecipientRole() === 'SALE' ? 'เบิกค่าคอมพนักงานขาย' : 'เบิกค่าคอมผู้ดูแล'
              }}
            </h2>
            <p class="mt-1 mb-0 text-sm text-slate-500">{{ booking.bookingNo }}</p>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeCommissionModal()"
          >
            <lucide-icon name="X" [size]="19" />
          </button>
        </div>
        <form [formGroup]="commissionForm" (ngSubmit)="saveCommission()">
          <div class="space-y-5 px-5 py-5 sm:px-6">
            <div
              class="grid gap-3 rounded-xl border border-sky-100 bg-sky-50/70 p-4 sm:grid-cols-3"
            >
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">ผู้รับสิทธิ์</p>
                <p class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ commissionRecipientName() }}
                </p>
              </div>
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">จำนวนผู้เดินทาง</p>
                <p class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ booking.travelerCount | number: '1.0-0' }} คน
                </p>
              </div>
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">ยอดเบิกทั้งหมด</p>
                <p class="mt-1 mb-0 text-lg font-bold text-sky-700">
                  {{ commissionPreviewTotal() | number: '1.2-2' }} บาท
                </p>
              </div>
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">ยอดหักคืนค้างอยู่</p>
                <p class="mt-1 mb-0 font-semibold text-red-600">
                  {{ commissionRecoveryOutstanding() | number: '1.2-2' }} บาท
                </p>
              </div>
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">ยอดที่จะหักครั้งนี้</p>
                <p class="mt-1 mb-0 font-semibold text-red-600">
                  -{{ commissionRecoveryDeduction() | number: '1.2-2' }} บาท
                </p>
              </div>
              <div>
                <p class="m-0 text-xs font-medium text-slate-500">ยอดรับสุทธิ (โดยประมาณ)</p>
                <p class="mt-1 mb-0 text-lg font-bold text-emerald-700">
                  {{ commissionNetPaymentPreview() | number: '1.2-2' }} บาท
                </p>
              </div>
              @if (commissionRecipientRole() === 'SALE') {
                <div>
                  <p class="m-0 text-xs font-medium text-slate-500">คอมพนักงานขาย (ยอดรวม)</p>
                  <p class="mt-1 mb-0 font-semibold text-slate-800">
                    {{ booking.saleCommission | number: '1.2-2' }} บาท
                  </p>
                  <p class="mt-1 mb-0 text-xs text-slate-500">
                    {{ saleCommissionPerPerson() | number: '1.2-2' }} บาท/คน
                  </p>
                </div>
                <div>
                  <p class="m-0 text-xs font-medium text-slate-500">คอมพิเศษ (ยอดรวม)</p>
                  <p class="mt-1 mb-0 font-semibold text-slate-800">
                    {{ booking.specialCommission | number: '1.2-2' }} บาท
                  </p>
                  <p class="mt-1 mb-0 text-xs text-slate-500">
                    {{ specialCommissionPerPerson() | number: '1.2-2' }} บาท/คน
                  </p>
                </div>
              } @else {
                <div>
                  <p class="m-0 text-xs font-medium text-slate-500">ค่าคอมผู้ดูแลต่อคน</p>
                  <p class="mt-1 mb-0 font-semibold text-slate-800">50.00 บาท</p>
                </div>
              }
            </div>
            <div class="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p class="m-0 text-xs text-slate-500">
                ยอดหักและยอดรับสุทธิจะคำนวณใหม่อีกครั้งเมื่อฝ่ายบัญชีอนุมัติจ่ายเงิน
              </p>
            </div>
            <div class="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p class="m-0 text-xs font-medium text-slate-500">เอกสารรับค่าคอม</p>
              @if (booking.commissionAttachmentPath) {
                <a
                  class="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:underline"
                  [href]="commissionAttachmentUrl(booking.commissionAttachmentPath)"
                  target="_blank"
                  rel="noopener"
                >
                  <lucide-icon name="Paperclip" [size]="15" /> เปิดเอกสารประกอบ
                </a>
              } @else {
                <p class="mt-1 mb-0 text-sm font-semibold text-amber-700">
                  ยังไม่มีเอกสารรับค่าคอม
                </p>
              }
            </div>
          </div>
          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closeCommissionModal()"
            >
              ยกเลิก</button
            ><button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:opacity-50"
              [disabled]="
                (!editingCommission() && !canCreateCommissionFor(commissionRecipientRole())) ||
                !hasCommissionRecipient(commissionRecipientRole()) ||
                commissionForm.invalid ||
                isSavingCommission()
              "
            >
              <lucide-icon name="Send" [size]="18" />
              {{ isSavingCommission() ? 'กำลังส่ง...' : 'ส่งเบิกค่าคอม' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isCollaboratorModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeCollaboratorModal()"
    >
      <section
        class="my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="collaborator-modal-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              ข้อมูล Booking
            </p>
            <h2 id="collaborator-modal-title" class="m-0 text-xl font-bold text-slate-800">
              {{ booking.collaboratorId ? 'เปลี่ยนผู้ดูแล' : 'เพิ่มผู้ดูแล' }}
            </h2>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeCollaboratorModal()"
          >
            <lucide-icon name="X" [size]="19" aria-hidden="true" />
          </button>
        </div>
        <form [formGroup]="collaboratorForm" (ngSubmit)="saveCollaborator()">
          <div class="px-5 py-6 sm:px-6">
            <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
              <span>พนักงานฝ่ายขาย <span class="text-red-500">*</span></span>
              <p-select
                class="h-11 w-full font-normal"
                formControlName="collaboratorId"
                [options]="salesStaffOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                filterBy="label"
                appendTo="body"
                [loading]="isLoadingSalesStaff()"
                placeholder="เลือกผู้ดูแล"
              />
            </label>
            @if (
              collaboratorForm.controls.collaboratorId.touched &&
              collaboratorForm.controls.collaboratorId.invalid
            ) {
              <small class="mt-1 block font-normal text-red-600">กรุณาเลือกผู้ดูแล</small>
            }
            @if (!isLoadingSalesStaff() && salesStaffOptions().length === 0) {
              <p class="mt-2 mb-0 text-sm text-slate-500">ไม่พบพนักงานฝ่ายขายที่ยังใช้งานอยู่</p>
            }
          </div>
          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
          >
            @if (booking.collaboratorId) {
              <button
                type="button"
                class="tw-button h-11 rounded-xl border border-red-200 bg-white px-5 text-[15px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                [disabled]="isSavingCollaborator()"
                (click)="deleteCollaborator()"
              >
                ลบผู้ดูแล
              </button>
            } @else {
              <span></span>
            }
            <div class="flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
                (click)="closeCollaboratorModal()"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                class="tw-button h-11 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:opacity-40"
                [disabled]="
                  isSavingCollaborator() ||
                  isLoadingSalesStaff() ||
                  salesStaffOptions().length === 0
                "
              >
                {{ isSavingCollaborator() ? 'กำลังบันทึก...' : 'บันทึกผู้ดูแล' }}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isMemberModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeMemberModal()"
    >
      <section
        class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-member-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              ข้อมูลผู้เดินทาง
            </p>
            <h2 id="add-member-title" class="m-0 text-xl font-bold text-slate-800">
              {{ editingTourMember() ? 'แก้ไขสมาชิกทัวร์' : 'เพิ่มสมาชิกทัวร์' }}
            </h2>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeMemberModal()"
          >
            <lucide-icon name="X" [size]="19" aria-hidden="true" />
          </button>
        </div>

        <form [formGroup]="memberForm" (ngSubmit)="saveTourMember()">
          <div class="grid gap-4 px-5 py-6 sm:px-6">
            <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
              <span>ชื่อ-นามสกุล <span class="text-red-500">*</span></span>
              <input
                pInputText
                class="h-11 w-full font-normal"
                formControlName="fullName"
                placeholder="กรอกชื่อและนามสกุล"
              />
              @if (isMemberFieldInvalid('fullName')) {
                <small class="text-xs font-normal leading-5 text-red-600"
                  >กรุณากรอกชื่อ-นามสกุล</small
                >
              }
            </label>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span
                  >เลขบัตรประชาชน / เลขประจำตัวผู้เสียภาษี <span class="text-red-500">*</span></span
                >
                <input
                  pInputText
                  class="h-11 w-full font-normal"
                  formControlName="citizenId"
                  maxlength="13"
                  placeholder="กรอก 9-13 ตัวอักษรหรือตัวเลข"
                />
                @if (isMemberFieldInvalid('citizenId')) {
                  <small class="text-xs font-normal leading-5 text-red-600"
                    >กรุณากรอก 9-13 ตัวอักษรหรือตัวเลข</small
                  >
                }
              </label>
              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>เบอร์โทร <span class="text-red-500">*</span></span>
                <input
                  pInputText
                  class="h-11 w-full font-normal"
                  formControlName="phoneNumber"
                  inputmode="tel"
                  placeholder="เช่น 0812345678"
                />
                @if (isMemberFieldInvalid('phoneNumber')) {
                  <small class="text-xs font-normal leading-5 text-red-600"
                    >กรุณากรอกเบอร์โทรให้ถูกต้อง</small
                  >
                }
              </label>
            </div>

            <label
              class="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700"
              for="isLeadTraveler"
            >
              <p-checkbox
                inputId="isLeadTraveler"
                formControlName="isLeadTraveler"
                [binary]="true"
              />
              <span
                ><span class="block">กำหนดเป็นผู้เดินทางหลัก</span
                ><small class="font-normal text-slate-500"
                  >ใช้เป็นผู้ติดต่อหลักของ Booking นี้</small
                ></span
              >
            </label>

            <div class="grid gap-1.5 text-sm font-semibold text-slate-700">
              <span>ไฟล์แนบหน้าพาสปอร์ต <span class="text-red-500">*</span></span>
              <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #passportUpload
                    mode="basic"
                    name="passport"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="maxPassportFileSize()"
                    [auto]="false"
                    (onSelect)="selectPassportFile($event)"
                    (onClear)="passportFile.set(null); passportFileName.set('')"
                  />
                  <button
                    type="button"
                    class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!passportUpload.files.length"
                    (click)="
                      passportUpload.clear(); passportFile.set(null); passportFileName.set('')
                    "
                  >
                    <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                  </button>
                </div>
                @if (editingTourMember()?.passportFileName; as oldFile) {
                  <a
                    [href]="passportUrl(oldFile)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-3 text-xs font-semibold text-sky-700 no-underline transition hover:bg-sky-50 hover:text-sky-900"
                    ><lucide-icon name="ExternalLink" [size]="15" aria-hidden="true" />ดูไฟล์เดิม</a
                  >
                }
                @if (passportFileName()) {
                  <p class="mt-2 mb-0 text-xs font-normal text-sky-700">{{ passportFileName() }}</p>
                }
                @if (booking.bookingType === 'GROUP_TOUR') {
                  <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                    เหมากรุ๊ป: รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 20 MB
                  </p>
                } @else {
                  <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                    จอยทัวร์: รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                  </p>
                }
              </div>
              @if (!passportFileName() && memberForm.touched) {
                <small class="text-xs font-normal leading-5 text-red-600"
                  >กรุณาแนบไฟล์หน้าพาสปอร์ต</small
                >
              }
            </div>
          </div>

          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closeMemberModal()"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)]"
              [disabled]="isSavingMember()"
            >
              <lucide-icon name="Check" [size]="18" aria-hidden="true" />
              {{
                isSavingMember()
                  ? 'กำลังบันทึก...'
                  : editingTourMember()
                    ? 'บันทึกการแก้ไข'
                    : 'บันทึกสมาชิก'
              }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isRefundModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closeRefundModal()"
      (keydown.escape)="closeRefundModal()"
    >
      <section
        class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-refund-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              {{ booking.bookingNo }}
            </p>
            <h2 id="add-refund-title" class="m-0 text-xl font-bold text-slate-800">
              ส่งเบิกคืนเงิน
            </h2>
            <p class="mt-1 mb-0 text-sm text-slate-500">
              กรอกข้อมูลผู้รับเงินและรายละเอียดการคืนเงิน
            </p>
          </div>
          <button
            type="button"
            class="tw-button grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closeRefundModal()"
          >
            <lucide-icon name="X" [size]="19" />
          </button>
        </div>

        <form [formGroup]="refundForm" (ngSubmit)="saveRefund()">
          <div class="grid gap-4 p-5 sm:grid-cols-2 sm:p-6">
            <label
              class="grid content-start gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>ชื่อผู้รับเงินคืน <span class="text-red-500">*</span></span>
              <input
                pInputText
                class="h-11 w-full font-normal"
                formControlName="refundName"
                placeholder="กรอกชื่อผู้รับเงินคืน"
              />
              @if (isRefundFieldInvalid('refundName')) {
                <small class="font-normal leading-5 text-red-600">กรุณากรอกชื่อผู้รับเงินคืน</small>
              }
            </label>

            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>ธนาคาร <span class="text-red-500">*</span></span>
              <p-select
                class="h-11 w-full font-normal"
                formControlName="bankName"
                [options]="bankOptions"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                filterBy="label"
                placeholder="เลือกธนาคาร"
              />
              @if (isRefundFieldInvalid('bankName')) {
                <small class="font-normal leading-5 text-red-600">กรุณาเลือกธนาคาร</small>
              }
            </label>

            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>เลขที่บัญชี <span class="text-red-500">*</span></span>
              <input
                pInputText
                class="h-11 w-full font-normal"
                formControlName="accountNumber"
                inputmode="numeric"
                placeholder="กรอกเลขที่บัญชี 6–20 หลัก"
              />
              @if (isRefundFieldInvalid('accountNumber')) {
                <small class="font-normal leading-5 text-red-600">
                  กรุณากรอกเลขที่บัญชีเป็นตัวเลข 6–20 หลัก
                </small>
              }
            </label>

            <label
              class="grid content-start gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>ประเภทการคืนเงิน <span class="text-red-500">*</span></span>
              <p-select
                class="h-11 w-full font-normal"
                formControlName="refundType"
                [options]="refundTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="เลือกประเภทการคืนเงิน"
                (onChange)="onRefundTypeChange($event.value)"
              />
              @if (isRefundFieldInvalid('refundType')) {
                <small class="font-normal leading-5 text-red-600">กรุณาเลือกประเภทการคืนเงิน</small>
              }
            </label>

            <label
              class="grid content-start gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>จำนวนเงินคืน <span class="text-red-500">*</span></span>
              <p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full"
                formControlName="amount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                [min]="1"
                [max]="refundAmountLimit()"
                [readonly]="refundForm.controls.refundType.value === 'FULL'"
                [invalid]="isRefundFieldInvalid('amount')"
                placeholder="0.00"
                (onInput)="checkRefundAmount($event.value)"
              />
              @if (refundForm.controls.refundType.value === 'FULL') {
                <small class="font-normal leading-5 text-sky-700">
                  ยอดชำระที่ตรวจสอบแล้วคงเหลือ โดยไม่หักค่าใช้จ่ายที่จ่ายแล้ว
                  {{ remainingFullRefundAmount() | number: '1.2-2' }} บาท
                </small>
              }
              @if (isRefundFieldInvalid('amount')) {
                @if (refundForm.controls.amount.hasError('max')) {
                  <small class="font-normal leading-5 text-red-600">
                    จำนวนเงินคืนต้องไม่เกินยอดที่คืนได้
                    {{ refundAmountLimit() | number: '1.2-2' }} บาท
                  </small>
                } @else {
                  <small class="font-normal leading-5 text-red-600">
                    กรุณากรอกจำนวนเงินคืนมากกว่า 0 บาท
                  </small>
                }
              }
            </label>

            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2">
              <span>เหตุผลการคืนเงิน <span class="text-red-500">*</span></span>
              <textarea
                pInputText
                class="min-h-24 w-full resize-y px-3 py-2.5 font-normal"
                formControlName="reason"
                maxlength="500"
                placeholder="ระบุเหตุผลการคืนเงิน"
              ></textarea>
              @if (isRefundFieldInvalid('reason')) {
                <small class="font-normal leading-5 text-red-600">กรุณาระบุเหตุผลการคืนเงิน</small>
              }
            </label>

            <div class="grid gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2">
              <span>เอกสาร <span class="text-red-500">*</span></span>
              <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #refundAttachmentUpload
                    mode="basic"
                    name="refundAttachment"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="5000000"
                    [auto]="false"
                    (onSelect)="selectRefundAttachment($event)"
                    (onClear)="clearRefundAttachment()"
                  />
                  <button
                    type="button"
                    class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!refundAttachmentUpload.files.length"
                    (click)="refundAttachmentUpload.clear(); clearRefundAttachment()"
                  >
                    <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                  </button>
                </div>
                @if (refundAttachmentFileName()) {
                  <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                    {{ refundAttachmentFileName() }}
                  </p>
                }
                <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                  รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                </p>
              </div>
              @if (!refundAttachmentFileName() && refundForm.touched) {
                <small class="font-normal leading-5 text-red-600">กรุณาแนบไฟล์ประกอบการคืนเงิน</small>
              }
            </div>
          </div>

          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closeRefundModal()"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              [disabled]="refundForm.invalid || !refundAttachmentFileName() || isSavingRefund()"
            >
              <lucide-icon name="Check" [size]="18" aria-hidden="true" />
              {{ isSavingRefund() ? 'กำลังบันทึก...' : 'ส่งเบิกคืนเงิน' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (isPaymentModalOpen() && !isBookingDeleted()) {
    <div
      class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
      (click)="closePaymentModal()"
    >
      <section
        class="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-payment-title"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
              ข้อมูลการรับชำระ
            </p>
            <h2 id="add-payment-title" class="m-0 text-xl font-bold text-slate-800">
              {{ editingPayment() ? 'แก้ไขการชำระเงิน' : 'แจ้งชำระเงิน' }}
            </h2>
          </div>
          <button
            type="button"
            class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="ปิดหน้าต่าง"
            (click)="closePaymentModal()"
          >
            <lucide-icon name="X" [size]="19" aria-hidden="true" />
          </button>
        </div>

        <form [formGroup]="paymentForm" (ngSubmit)="savePayment()">
          <div class="grid gap-4 px-5 py-6 sm:grid-cols-2 sm:px-6">
            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>ลำดับ</span>
              <input
                class="h-11 rounded-xl border border-slate-200 bg-slate-100 px-3 font-normal text-slate-500"
                [value]="editingPayment()?.receiptNumber || payments().length + 1"
                readonly
              />
            </label>
            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>ชื่อผู้ชำระเงิน <span class="text-red-500">*</span></span>
              <input
                pInputText
                class="h-11 w-full font-normal"
                formControlName="payerName"
                placeholder="กรอกชื่อผู้ชำระเงิน"
              />
              @if (isPaymentFieldInvalid('payerName')) {
                <small class="font-normal leading-5 text-red-600">กรุณากรอกชื่อผู้ชำระเงิน</small>
              }
            </label>
            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>ประเภทการชำระเงิน <span class="text-red-500">*</span></span>
              <p-select
                class="h-11 w-full font-normal"
                formControlName="paymentType"
                [options]="paymentTypeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="เลือกประเภทการชำระเงิน"
              />
              @if (isPaymentFieldInvalid('paymentType')) {
                <small class="font-normal leading-5 text-red-600"
                  >กรุณาเลือกประเภทการชำระเงิน</small
                >
              }
            </label>
            <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
              <span>งวดการชำระ <span class="text-red-500">*</span></span>
              <p-select
                class="h-11 w-full font-normal"
                formControlName="paymentStage"
                [options]="paymentStageOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="เลือกงวดการชำระ"
              />
              @if (isPaymentFieldInvalid('paymentStage')) {
                <small class="font-normal leading-5 text-red-600">กรุณาเลือกงวดการชำระ</small>
              }
            </label>
            @if (paymentForm.controls.paymentType.value === 'CREDIT_CARD') {
              <fieldset class="grid gap-2 sm:col-span-2">
                <legend class="mb-1 text-sm font-semibold text-slate-700">
                  ค่าธรรมเนียมบัตรเครดิต <span class="text-red-500">*</span>
                </legend>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  @for (option of creditCardFeeOptions; track option.value) {
                    <label
                      class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-slate-700"
                      [for]="'credit-card-fee-' + option.value"
                    >
                      <p-radiobutton
                        name="creditCardFeePercent"
                        formControlName="creditCardFeePercent"
                        [value]="option.value"
                        [inputId]="'credit-card-fee-' + option.value"
                      />
                      <span>{{ option.label }}</span>
                    </label>
                  }
                </div>
                @if (
                  paymentForm.controls.creditCardFeePercent.touched &&
                  paymentForm.controls.creditCardFeePercent.value === null
                ) {
                  <small class="font-normal leading-5 text-red-600"
                    >กรุณาเลือกเปอร์เซ็นต์ค่าธรรมเนียมบัตรเครดิต</small
                  >
                }
              </fieldset>

              <fieldset class="grid gap-2 sm:col-span-2">
                <legend class="mb-1 text-sm font-semibold text-slate-700">
                  ภาระค่าธรรมเนียม <span class="text-red-500">*</span>
                </legend>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  @for (option of creditCardFeeResponsibilityOptions; track option.value) {
                    <label
                      class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal text-slate-700"
                      [for]="'credit-card-fee-responsibility-' + option.value"
                    >
                      <p-radiobutton
                        name="creditCardFeeResponsibility"
                        formControlName="creditCardFeeResponsibility"
                        [value]="option.value"
                        [inputId]="'credit-card-fee-responsibility-' + option.value"
                      />
                      <span>{{ option.label }}</span>
                    </label>
                  }
                </div>
                @if (
                  paymentForm.controls.creditCardFeeResponsibility.touched &&
                  paymentForm.controls.creditCardFeeResponsibility.value === null
                ) {
                  <small class="font-normal leading-5 text-red-600"
                    >กรุณาเลือกผู้รับภาระค่าธรรมเนียม</small
                  >
                }
              </fieldset>
            }

            <label
              class="grid content-start gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2"
            >
              <span>จำนวนเงิน <span class="text-red-500">*</span></span>
              <p-inputnumber
                class="w-full font-normal"
                inputStyleClass="h-11 w-full"
                formControlName="amount"
                mode="currency"
                currency="THB"
                locale="th-TH"
                [min]="1"
                [max]="paymentAmountLimit()"
                placeholder="0.00"
                (onInput)="checkPaymentAmount($event.value)"
              />
              @if (isPaymentFieldInvalid('amount')) {
                @if (paymentForm.controls.amount.hasError('max')) {
                  <small class="font-normal leading-5 text-red-600">
                    จำนวนเงินต้องไม่เกิน {{ paymentAmountLimit() | number: '1.2-2' }} บาท
                  </small>
                } @else {
                  <small class="font-normal leading-5 text-red-600"
                    >กรุณากรอกจำนวนเงินมากกว่า 0</small
                  >
                }
              }
            </label>
            <div class="sm:col-span-2">
              <label
                class="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700"
                for="has-withholding-tax"
              >
                <p-checkbox
                  inputId="has-withholding-tax"
                  formControlName="hasWithholdingTax"
                  [binary]="true"
                  (onChange)="onWithholdingTaxChange($event.checked)"
                />
                <span>หัก ณ ที่จ่าย</span>
              </label>

              @if (paymentForm.controls.hasWithholdingTax.value) {
                <label class="mt-3 grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                  <span>ยอดหัก ณ ที่จ่าย</span>
                  <p-inputnumber
                    class="w-full font-normal"
                    inputStyleClass="h-11 w-full"
                    formControlName="withholdingTaxAmount"
                    mode="currency"
                    currency="THB"
                    locale="th-TH"
                    [min]="0"
                    placeholder="0.00"
                  />
                  @if (isPaymentFieldInvalid('withholdingTaxAmount')) {
                    <small class="font-normal leading-5 text-red-600"
                      >ยอดหัก ณ ที่จ่ายต้องไม่น้อยกว่า 0 บาท</small
                    >
                  }
                </label>
              }
            </div>

            <div class="grid gap-4 sm:col-span-2 sm:grid-cols-2">
              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>วันที่ชำระเงิน <span class="text-red-500">*</span></span>
                <p-datepicker
                  class="w-full font-normal"
                  formControlName="paidDate"
                  dateFormat="dd/mm/yy"
                  placeholder="เลือกวันที่ชำระเงิน"
                  [showIcon]="true"
                  [readonlyInput]="true"
                  appendTo="body"
                />
                @if (isPaymentFieldInvalid('paidDate')) {
                  <small class="font-normal leading-5 text-red-600">กรุณาเลือกวันที่ชำระเงิน</small>
                }
              </label>
              <label class="grid content-start gap-1.5 text-sm font-semibold text-slate-700">
                <span>เวลา <span class="text-red-500">*</span></span>
                <p-datepicker
                  class="w-full font-normal"
                  formControlName="paidTime"
                  [timeOnly]="true"
                  hourFormat="24"
                  placeholder="เลือกเวลาชำระเงิน"
                  [showIcon]="true"
                  [readonlyInput]="true"
                  appendTo="body"
                />
                @if (isPaymentFieldInvalid('paidTime')) {
                  <small class="font-normal leading-5 text-red-600">กรุณาเลือกเวลาชำระเงิน</small>
                }
              </label>
            </div>
            <div class="grid gap-1.5 text-sm font-semibold text-slate-700 sm:col-span-2">
              <span>ไฟล์สลิป <span class="text-red-500">*</span></span>
              <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p-fileupload
                    #slipUpload
                    mode="basic"
                    name="paymentSlip"
                    chooseLabel="เลือกไฟล์"
                    accept=".pdf,.jpg,.jpeg,.png"
                    [maxFileSize]="5000000"
                    [auto]="false"
                    (onSelect)="selectPaymentSlip($event)"
                    (onClear)="paymentSlipFile.set(null); paymentSlipFileName.set('')"
                  /><button
                    type="button"
                    class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                    [disabled]="!slipUpload.files.length"
                    (click)="
                      slipUpload.clear(); paymentSlipFile.set(null); paymentSlipFileName.set('')
                    "
                  >
                    <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                  </button>
                </div>
                @if (paymentSlipFileName()) {
                  <p class="mt-2 mb-0 text-xs font-normal text-sky-700">
                    {{ paymentSlipFileName() }}
                  </p>
                }
                @if (editingPayment()?.slipFileName; as oldFile) {
                  <a
                    [href]="passportUrl(oldFile)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-3 text-xs font-semibold text-sky-700 no-underline transition hover:bg-sky-50 hover:text-sky-900"
                    ><lucide-icon name="ExternalLink" [size]="15" aria-hidden="true" />ดูไฟล์เดิม</a
                  >
                }
                <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                  รองรับไฟล์ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
                </p>
              </div>
              @if (!paymentSlipFileName() && paymentForm.touched) {
                <small class="font-normal leading-5 text-red-600">กรุณาแนบไฟล์สลิป</small>
              }
            </div>
          </div>

          <div
            class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
          >
            <button
              type="button"
              class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
              (click)="closePaymentModal()"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-[15px] font-semibold text-white hover:bg-[var(--theme-hover)]"
              [disabled]="paymentForm.invalid || !paymentSlipFileName() || isSavingPayment()"
            >
              <lucide-icon name="Check" [size]="18" aria-hidden="true" />
              {{
                isSavingPayment()
                  ? 'กำลังบันทึก...'
                  : editingPayment()
                    ? 'บันทึกการแก้ไข'
                    : 'บันทึกการชำระเงิน'
              }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }
} @else if (!isLoading() && isNotFound()) {
  <section class="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
    <lucide-icon name="CircleHelp" [size]="32" class="mx-auto text-slate-400" />
    <h2 class="mt-3 text-lg font-bold text-slate-800">ไม่พบข้อมูล Booking</h2>
    <p class="text-sm text-slate-500">ไม่พบรายการที่มีรหัส {{ bookingId }}</p>
    <a
      [routerLink]="bookingListUrl()"
      class="mt-4 inline-flex rounded-xl bg-[var(--theme-color)] px-4 py-2.5 text-sm font-semibold text-white no-underline hover:bg-[var(--theme-hover)]"
      >กลับหน้ารายการ</a
    >
  </section>
}

@if (isBookingCommissionDocumentModalOpen()) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="isBookingCommissionDocumentModalOpen.set(false)"
    (keydown.escape)="isBookingCommissionDocumentModalOpen.set(false)"
  >
    <section
      class="my-auto w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-commission-document-title"
      (click)="$event.stopPropagation()"
    >
      <div
        class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6"
      >
        <div>
          <p class="mb-1 text-xs font-semibold text-sky-600">{{ booking()?.bookingNo }}</p>
          <h2 id="booking-commission-document-title" class="m-0 text-xl font-bold text-slate-800">
            เอกสารรับค่าคอม
          </h2>
          <p class="mt-1 mb-0 text-sm text-slate-500">
            แนบครั้งเดียว ใช้ร่วมกันสำหรับ Sale และผู้ดูแลรายการนี้
          </p>
        </div>
        <button
          type="button"
          class="tw-button grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="isBookingCommissionDocumentModalOpen.set(false)"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <div class="space-y-4 p-5 sm:p-6">
        @if (canManageBooking()) {
          <div class="grid content-start gap-1 text-sm font-semibold text-slate-700">
            <span>แนบไฟล์</span>
            <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <p-fileupload
                  #bookingCommissionDocumentUpload
                  mode="basic"
                  name="bookingCommissionAttachment"
                  chooseLabel="เลือกไฟล์"
                  accept=".pdf,.jpg,.jpeg,.png"
                  [maxFileSize]="5000000"
                  [auto]="false"
                  invalidFileSizeMessageSummary="ไฟล์มีขนาดใหญ่เกินไป"
                  invalidFileSizeMessageDetail="กรุณาเลือกไฟล์ขนาดไม่เกิน 5 MB"
                  invalidFileTypeMessageSummary="ประเภทไฟล์ไม่รองรับ"
                  invalidFileTypeMessageDetail="กรุณาเลือก PDF, JPG หรือ PNG"
                  (onSelect)="selectBookingCommissionDocument($event)"
                  (onClear)="clearBookingCommissionDocument()"
                />
                <button
                  type="button"
                  class="tw-button inline-flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:opacity-40"
                  [disabled]="!bookingCommissionDocumentUpload.files.length"
                  (click)="
                    bookingCommissionDocumentUpload.clear(); clearBookingCommissionDocument()
                  "
                >
                  <lucide-icon name="X" [size]="16" /> ยกเลิกไฟล์
                </button>
              </div>
              @if (bookingCommissionDocumentFile(); as file) {
                <p class="mt-2 mb-0 break-all text-xs font-normal text-sky-700">{{ file.name }}</p>
              }
              <p class="mt-2 mb-0 text-xs font-normal text-slate-500">
                รองรับ PDF, JPG และ PNG ขนาดไม่เกิน 5 MB
              </p>
            </div>
          </div>
        }
      </div>
      <div
        class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          (click)="closeBookingCommissionDocumentModal()"
        >
          ยกเลิก
        </button>
        <button
          type="button"
          class="tw-button inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--theme-color)] px-5 text-sm font-semibold text-white hover:bg-[var(--theme-hover)]"
          [disabled]="!bookingCommissionDocumentFile() || isSavingBookingCommissionDocument()"
          (click)="saveBookingCommissionDocument()"
        >
          <lucide-icon name="Save" [size]="17" />
          {{ isSavingBookingCommissionDocument() ? 'กำลังบันทึก...' : 'บันทึก' }}
        </button>
      </div>
    </section>
  </div>
}
